import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Download, Share, Eye, Pen, ScanLine } from 'lucide-react';
import EmailPreviewContent from './EmailPreviewContent';
import ShareModal from './ShareModal';
import { Checkbox } from './FieldComponents';
import heic2any from 'heic2any';
import '../utils/promiseWithResolversPolyfill';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import '../styles/FileViewer.css';
import { useNavVariant } from '../App';
import { beginDownloadNotification, markDownloadNotificationStarted, dismissDownloadNotification } from '../utils/downloadNotifications';
import { buildFileViewerDownloadUrl, fetchFileViewerSource, hasFileViewerRecordId } from './fileViewerSource';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

const isSafariBrowser = () => {
    if (typeof navigator === 'undefined') return false;

    const userAgent = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    const isAppleVendor = /Apple/i.test(vendor);
    const isSafari = /Safari/i.test(userAgent);
    const isOtherBrowser = /Chrome|CriOS|Chromium|FxiOS|Firefox|Edg|EdgiOS|OPR|Opera|Android/i.test(userAgent);

    return isAppleVendor && isSafari && !isOtherBrowser;
};

const FileViewer = () => {
    const { fileId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const targetParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const targetRegistryId = targetParams.get('registry_id');
    const targetChunkIndex = targetParams.get('chunk_index');
    const targetChunkToken = targetParams.get('chunk_token');
    const targetCaseId = targetParams.get('case_id');
    const targetS3Key = targetParams.get('s3_key');
    const targetFileName = targetParams.get('file_name');
    const viewerMode = targetParams.get('mode');
    const isRedactionMode = viewerMode === 'redact';
    const shouldPdfHighlightChunk = fileId !== 's3'
        && !targetS3Key
        && targetParams.get('pdf_highlight') === '1'
        && !!targetRegistryId
        && targetChunkIndex !== null;
    const [file, setFile] = useState(null);
    const [fileContent, setFileContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareTarget, setShareTarget] = useState(null);
    const [shareLink, setShareLink] = useState("");
    const navVariant = useNavVariant();
    const [pageCount, setPageCount] = useState(0);
    const [heicConversionState, setHeicConversionState] = useState({
        isConverting: false,
        convertedUrl: null,
        conversionError: null
    });
    const [emailPreviewState, setEmailPreviewState] = useState({
        isLoading: false,
        error: null
    });
    const [emailPreviewData, setEmailPreviewData] = useState(null);
    const [emailPdfPreviewState, setEmailPdfPreviewState] = useState({
        isLoading: false,
        error: null,
        url: null
    });
    const [headerHidden, setHeaderHidden] = useState(false);
    const [officeEditState, setOfficeEditState] = useState({
        isLoading: false,
        error: null
    });
    const [chunkTargetState, setChunkTargetState] = useState({
        isLoading: false,
        error: null,
        data: null
    });
    const [temporaryPdfState, setTemporaryPdfState] = useState({
        isLoading: false,
        error: null,
        url: null,
        key: null
    });
    const [pdfHighlightState, setPdfHighlightState] = useState({
        status: 'idle',
        pageNumber: null,
        message: '',
        debug: null
    });
    const [showChunkHighlights, setShowChunkHighlights] = useState(true);
    const [redactionBoxes, setRedactionBoxes] = useState([]);
    const [redactionDraftState, setRedactionDraftState] = useState({
        isSaving: false,
        error: null,
        lastSavedAt: null,
    });
    const shouldApplyChunkHighlights = shouldPdfHighlightChunk && showChunkHighlights;
    const showChunkHighlightsRef = useRef(true);
    const pdfScrollRef = useRef(null);
    const pdfCanvasContainerRef = useRef(null);
    const pdfThumbsContainerRef = useRef(null);
    const pdfWorkerUrlRef = useRef(null);
    const lastActiveThumbRef = useRef(null);
    const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
    const [isPageEditing, setIsPageEditing] = useState(false);
    const [pageInputValue, setPageInputValue] = useState('1');
    const [forceNativePdfViewer, setForceNativePdfViewer] = useState(false);
    const BASE_PDF_SCALE = 0.5;
    const MIN_PDF_SCALE = BASE_PDF_SCALE * 0.5;
    const MAX_PDF_SCALE = 3;
    const MIN_PDF_FIT_SCALE = 0.1;
    const MIN_PDF_RENDER_SCALE = 0.1;
    const MAX_PDF_CANVAS_PIXEL_RATIO = 2.5;
    const MAX_PDF_CANVAS_PIXELS = 16000000;
    const [pdfScale, setPdfScale] = useState(BASE_PDF_SCALE);
    const [pdfRenderScale, setPdfRenderScale] = useState(BASE_PDF_SCALE);
    const [pdfDisplayedScale, setPdfDisplayedScale] = useState(BASE_PDF_SCALE);
    const [isZoomEditing, setIsZoomEditing] = useState(false);
    const [zoomInputValue, setZoomInputValue] = useState('100');
    const [pdfRenderState, setPdfRenderState] = useState({
        isRendering: false,
        error: null
    });
    const [pdfProgress, setPdfProgress] = useState({
        isLoadingDocument: false,
        downloadPercent: 0,
        renderedPages: 0,
        totalPages: 0,
        renderedThumbs: 0,
        totalThumbs: 0,
        mode: 'standard',
        isWindowRendering: false,
        windowRenderedPages: 0,
        windowTotalPages: 0,
        windowStartPage: 0,
        windowEndPage: 0,
        windowAnchorPage: 0
    });
    const useNativePdfViewer = useMemo(() => !isSafariBrowser(), []);
    const pdfCurrentPageRef = useRef(1);
    const PDF_OPTIMIZED_MODE_THRESHOLD = 60;
    const PDF_RENDER_WINDOW_SIZE = 25;
    const PDF_RENDER_WINDOW_RADIUS = Math.floor(PDF_RENDER_WINDOW_SIZE / 2);
    const PDF_RETAIN_WINDOW_PADDING = 6;
    const pdfVirtualizerRef = useRef(null);
    const pdfHighlightAppliedRef = useRef(null);
    const pdfChunkAlignmentRef = useRef(null);
    const pdfWindowProgressRef = useRef({
        startPage: 0,
        endPage: 0,
        anchorPage: 0
    });
    
    const handleBackOrClose = () => {
        // Check if this tab was opened as a new tab/window
        // We can detect this by checking if the window was opened via window.open
        // or if there's no history to go back to
        if (window.history.length <= 1 || window.opener) {
            // This is likely a new tab/window, so close it
            window.close();
        } else {
            // This is a regular navigation, go back
            navigate(-1);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const fetchFile = async () => {
            try {
                const fileWithUrl = await fetchFileViewerSource({
                    fileId,
                    caseId: targetCaseId,
                    s3Key: targetS3Key,
                    fileName: targetFileName,
                });
                if (cancelled) return;

                setFile(fileWithUrl);
                console.log('File loaded with URL:', fileWithUrl.url);

                if (fileWithUrl.page_count !== undefined) {
                    setPageCount(fileWithUrl.page_count);
                }
            } catch (err) {
                if (cancelled) return;
                setError(err.message || 'Failed to load file');
                console.error('Error fetching file:', err);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        if (fileId || targetS3Key) {
            fetchFile();
        }

        return () => {
            cancelled = true;
        };
    }, [fileId, targetCaseId, targetS3Key, targetFileName]);

    useEffect(() => {
        const handleResizeObserverError = (event) => {
            const message = event?.message || '';
            if (message.includes('ResizeObserver loop completed')) {
                event.preventDefault();
            }
        };

        window.addEventListener('error', handleResizeObserverError);
        return () => {
            window.removeEventListener('error', handleResizeObserverError);
        };
    }, []);

    // Load text content for plain text-like files
    useEffect(() => {
        document.title = file ? `${file.file_name} | CaseDB` : "File Viewer | CaseDB";

        if (file && file.url) {
            const fileExtension = file.file_name?.split(".").pop().toLowerCase();
            if (["txt", "csv", "json", "ics", "log", "md", "xml", "html", "htm"].includes(fileExtension)) {
                setFileContent(null);
                fetch(file.url, { credentials: 'include' })
                    .then(res => res.text())
                    .then(text => setFileContent(text))
                    .catch(() => setFileContent("⚠ Unable to load content."));
            }
        }
    }, [file]);

    // Load cached PDF previews for email files, with readable email fallback if conversion fails
    useEffect(() => {
        if (!file || shouldPdfHighlightChunk) {
            if (shouldPdfHighlightChunk) {
                setEmailPreviewState({ isLoading: false, error: null });
                setEmailPreviewData(null);
                setEmailPdfPreviewState({ isLoading: false, error: null, url: null });
            }
            return;
        }

        const fileExtension = file.file_name?.split(".").pop().toLowerCase();
        if (!["eml", "msg"].includes(fileExtension)) {
            setEmailPreviewState({ isLoading: false, error: null });
            setEmailPreviewData(null);
            setEmailPdfPreviewState({ isLoading: false, error: null, url: null });
            return;
        }

        const fileKey = file.file_path || file.key || file.path || file.file_name;
        if (!fileKey) {
            setEmailPreviewData(null);
            setEmailPreviewState({
                isLoading: false,
                error: `No file path available for ${fileExtension.toUpperCase()} preview.`
            });
            setEmailPdfPreviewState({
                isLoading: false,
                error: `No file path available for ${fileExtension.toUpperCase()} PDF preview.`,
                url: null
            });
            return;
        }

        let cancelled = false;

        const fetchReadableEmailPreview = async () => {
            setEmailPreviewState({ isLoading: true, error: null });
            setEmailPreviewData(null);
            setFileContent(null);

            try {
                const response = await fetch(`https://api.casedb.co/email-preview.php?fileKey=${encodeURIComponent(fileKey)}&cases=true`, { credentials: 'include' });
                const data = await response.json();
                if (!response.ok || !data?.success || !data?.preview) {
                    throw new Error(data?.error || `Failed to load ${fileExtension.toUpperCase()} preview`);
                }
                if (cancelled) return;
                setEmailPreviewData(data.preview);
                setEmailPreviewState({ isLoading: false, error: null });
            } catch (err) {
                if (cancelled) return;
                console.error('Email preview error:', err);
                setEmailPreviewState({
                    isLoading: false,
                    error: err.message || `Unable to preview ${fileExtension.toUpperCase()} file.`
                });
            }
        };

        const fetchEmailPdfPreview = async () => {
            setEmailPdfPreviewState({ isLoading: true, error: null, url: null });
            setEmailPreviewState({ isLoading: false, error: null });
            setEmailPreviewData(null);

            try {
                const params = new URLSearchParams();
                params.append('fileKey', fileKey);
                if (file.id) {
                    params.append('fileId', String(file.id));
                }
                params.append('cases', 'true');

                const response = await fetch(`https://api.casedb.co/email-preview-pdf.php?${params.toString()}`, { credentials: 'include' });
                const data = await response.json();
                if (!response.ok || !data?.success || !data?.url) {
                    throw new Error(data?.error || `Failed to load ${fileExtension.toUpperCase()} PDF preview`);
                }

                if (cancelled) return;
                setEmailPdfPreviewState({ isLoading: false, error: null, url: data.url });
            } catch (err) {
                if (cancelled) return;
                console.error('Email PDF preview error:', err);
                setEmailPdfPreviewState({
                    isLoading: false,
                    error: err.message || `Unable to create ${fileExtension.toUpperCase()} PDF preview.`,
                    url: null
                });
                await fetchReadableEmailPreview();
            }
        };

        fetchEmailPdfPreview();

        return () => {
            cancelled = true;
        };
    }, [file, shouldPdfHighlightChunk]);

    // Convert HEIC files for viewing
    const convertHeicFile = async (url) => {
        setHeicConversionState({ isConverting: true, convertedUrl: null, conversionError: null });
        
        try {
            // Fetch the HEIC file as a blob
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error('Failed to fetch HEIC file');
            }
            
            const heicBlob = await response.blob();
            
            // Convert HEIC to JPEG
            const jpegBlob = await heic2any({
                blob: heicBlob,
                toType: "image/jpeg",
                quality: 0.9
            });
            
            // Create object URL for the converted image
            const convertedUrl = URL.createObjectURL(jpegBlob instanceof Array ? jpegBlob[0] : jpegBlob);
            
            setHeicConversionState({ 
                isConverting: false, 
                convertedUrl, 
                conversionError: null 
            });
            
        } catch (error) {
            console.error('HEIC conversion error:', error);
            setHeicConversionState({ 
                isConverting: false, 
                convertedUrl: null, 
                conversionError: error.message 
            });
        }
    };

    // Trigger HEIC conversion when needed
    useEffect(() => {
        if (file && file.url) {
            const fileExtension = file.file_name?.split(".").pop().toLowerCase();
            if (fileExtension === "heic") {
                convertHeicFile(file.url);
            }
        }
    }, [file]);

    // Cleanup converted HEIC URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            if (heicConversionState.convertedUrl) {
                URL.revokeObjectURL(heicConversionState.convertedUrl);
            }
        };
    }, [heicConversionState.convertedUrl]);

    // Monitor if header actions are visible (for PDF viewers with hidden toolbars)
    /*useEffect(() => {
        const checkHeaderVisibility = () => {
            const actionsDiv = document.querySelector('.file-viewer-actions');
            const fallbackBtn = document.querySelector('.file-viewer-pdf-fallback');
            
            if (actionsDiv && fallbackBtn) {
                const isVisible = actionsDiv.offsetParent !== null;
                setHeaderHidden(!isVisible);
                if (!isVisible) {
                    fallbackBtn.classList.add('show');
                } else {
                    fallbackBtn.classList.remove('show');
                }
            }
        };

        // Check immediately and on window resize
        checkHeaderVisibility();
        window.addEventListener('resize', checkHeaderVisibility);
        
        return () => {
            window.removeEventListener('resize', checkHeaderVisibility);
        };
    }, [file]);*/

    const handleDownload = async () => {
        if (!file) return;
        
        const notificationId = beginDownloadNotification(file.file_name || 'download');
        try {
            // Properly encode filename to prevent corruption
            const filename = file.file_name || 'download';
            const downloadRequestUrl = buildFileViewerDownloadUrl(file);

            if (!downloadRequestUrl) {
                alert('Unable to download: File path not available');
                return;
            }

            const response = await fetch(downloadRequestUrl, {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to get signed URL');
            }

            const data = await response.json();

            if (!data.success || !data.url) {
                alert('Failed to generate download link');
                return;
            }

            // Use blob download method for better browser compatibility
            const fileResponse = await fetch(data.url);
            if (!fileResponse.ok) {
                throw new Error('Failed to download file');
            }

            const blob = await fileResponse.blob();
            const blobUrl = URL.createObjectURL(blob);
            
            // Create and trigger download link
            const link = document.createElement('a');
            link.href = blobUrl;
            // Let server Content-Disposition header handle the filename
            link.download = filename;
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            }, 100);
            markDownloadNotificationStarted(notificationId);
            
        } catch (error) {
            dismissDownloadNotification(notificationId);
            console.error('Download error:', error);
            alert('Download failed: ' + error.message);
        }
    };

    const handleShare = () => {
        setShareTarget(file);
        setShowShareModal(true);
    };

    const handleOfficeEdit = async () => {
        if (!file?.id) return;

        setOfficeEditState({ isLoading: true, error: null });

        try {
            const response = await fetch(`https://api.casedb.co/edit-session.php/${file.id}/editing`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.error || result.message || 'Failed to create editing session');
            }

            const editUrl = result.editUrl || result.directUrl || result.webEditUrl || result.fallbackUrl;
            if (!editUrl) {
                throw new Error('No edit URL returned from Microsoft integration');
            }

            window.open(editUrl, '_blank', 'noopener');
            setOfficeEditState({ isLoading: false, error: null });
        } catch (err) {
            console.error('Office edit error:', err);
            setOfficeEditState({ isLoading: false, error: err.message || 'Unable to open Office editor' });
            alert(`Unable to open Office editor: ${err.message || 'Unknown error'}`);
        }
    };

    const createShareableLink = async (expirationDays, allowUpload = false, password = null) => {
        try {
            const response = await fetch('https://api.casedb.co/create-share-link.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    file_id: file.id,
                    file_path: file.file_path,
                    file_name: file.file_name,
                    expiration_days: expirationDays,
                    allow_upload: allowUpload,
                    password: password,
                    type: 'file'
                })
            });

            const data = await response.json();
            if (data.success) {
                setShareLink(data.share_url);
                return data;
            } else {
                throw new Error(data.message || 'Failed to create share link');
            }
        } catch (error) {
            console.error('Error creating share link:', error);
            throw error;
        }
    };

    const copyCurrentUrl = () => {
        navigator.clipboard.writeText(window.location.href);
        alert('File URL copied to clipboard');
        setShowShareModal(false);
    };

    const handlePdfPrint = () => {
        const printUrl = temporaryPdfState.url || file?.url;
        if (!printUrl) return;
        const printWindow = window.open(printUrl, '_blank', 'noopener');
        if (!printWindow) return;
        const triggerPrint = () => {
            try {
                printWindow.focus();
                printWindow.print();
            } catch (err) {
                console.warn('Print failed', err);
            }
        };
        printWindow.addEventListener('load', triggerPrint);
    };

    const scrollToPdfPage = (pageNumber) => {
        const container = pdfScrollRef.current;
        if (!container) return;
        const target = container.querySelector(`.pdf-page[data-page-number="${pageNumber}"]`);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const capturePdfScrollAnchor = () => {
        const scrollContainer = pdfScrollRef.current;
        if (!scrollContainer) return null;

        const pages = scrollContainer.querySelectorAll('.pdf-page');
        if (!pages.length) {
            return {
                pageNumber: pdfCurrentPageRef.current || 1,
                pageTopViewportOffset: 0
            };
        }

        const currentPageNumber = pdfCurrentPageRef.current || 1;
        const activePage =
            scrollContainer.querySelector(`.pdf-page[data-page-number="${currentPageNumber}"]`) || pages[0];

        return {
            pageNumber: Number(activePage.dataset.pageNumber || currentPageNumber),
            pageTopViewportOffset: activePage.offsetTop - scrollContainer.scrollTop
        };
    };

    const restorePdfScrollAnchor = (anchor, fallbackPageNumber) => {
        const scrollContainer = pdfScrollRef.current;
        if (!scrollContainer) return;

        const pageNumber = anchor?.pageNumber || fallbackPageNumber || 1;
        const target = scrollContainer.querySelector(`.pdf-page[data-page-number="${pageNumber}"]`);

        if (!target) {
            const fallbackTarget = scrollContainer.querySelector(`.pdf-page[data-page-number="${fallbackPageNumber}"]`);
            if (fallbackTarget) {
                scrollContainer.scrollTop = fallbackTarget.offsetTop;
            }
            return;
        }

        const pageTopViewportOffset = Number.isFinite(anchor?.pageTopViewportOffset)
            ? anchor.pageTopViewportOffset
            : 0;
        const targetOffset = target.offsetTop - pageTopViewportOffset;
        scrollContainer.scrollTop = Math.max(0, targetOffset);
    };

    const goToPdfPage = (pageNumber) => {
        const maxPage = Math.max(1, Number(totalPdfPages || pageCount || 1));
        const parsedPage = Number(pageNumber);
        if (!Number.isFinite(parsedPage)) return;
        const clampedPage = Math.max(1, Math.min(maxPage, Math.round(parsedPage)));
        setPdfCurrentPage(clampedPage);
        setPageInputValue(String(clampedPage));
        scrollToPdfPage(clampedPage);
    };

    const commitZoomInput = () => {
        const parsed = Number(String(zoomInputValue).replace(/[^0-9.]/g, ''));
        if (!Number.isFinite(parsed)) {
            setIsZoomEditing(false);
            return;
        }
        const targetScale = BASE_PDF_SCALE * (parsed / 100);
        const clamped = Math.max(MIN_PDF_SCALE, Math.min(MAX_PDF_SCALE, Number(targetScale.toFixed(3))));
        setPdfScale(clamped);
        setIsZoomEditing(false);
    };

    const commitPageInput = () => {
        const parsed = Number(String(pageInputValue).replace(/[^0-9]/g, ''));
        if (!Number.isFinite(parsed)) {
            setPageInputValue(String(pdfCurrentPage));
            setIsPageEditing(false);
            return;
        }

        goToPdfPage(parsed);
        setIsPageEditing(false);
    };

    const getPdfCanvasPixelRatio = (width, height) => {
        const devicePixelRatio = window.devicePixelRatio || 1;
        const cappedDeviceRatio = Math.max(1, Math.min(MAX_PDF_CANVAS_PIXEL_RATIO, devicePixelRatio));
        const viewportPixels = Math.max(1, width * height);
        const memorySafeRatio = Math.sqrt(MAX_PDF_CANVAS_PIXELS / viewportPixels);

        return Math.max(1, Math.min(cappedDeviceRatio, memorySafeRatio));
    };

    const fileExtension = file?.file_name?.split(".").pop()?.trim().toLowerCase();
    const hasFileRecordId = hasFileViewerRecordId(file);
    const isOfficeDocument = fileExtension && ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(fileExtension);
    const canConvertToPdfPreview = fileExtension && ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "eml", "msg", "heic", "heif", "tif", "tiff"].includes(fileExtension);
    const shouldUseTemporaryPdfPreview = shouldPdfHighlightChunk;
    const activePdfUrl = shouldUseTemporaryPdfPreview ? (temporaryPdfState.url || file?.url) : file?.url;
    const viewerExtension = shouldUseTemporaryPdfPreview && temporaryPdfState.url ? "pdf" : fileExtension;
    const totalPdfPages = file?.page_count || pageCount || 0;
    const canUseRedactionMode = isRedactionMode && viewerExtension === 'pdf' && hasFileRecordId;
    const shouldUseNativePdfViewer = !canUseRedactionMode && (forceNativePdfViewer || (useNativePdfViewer && !shouldPdfHighlightChunk));
    let content;

    const normalizePdfSearchText = (value) => String(value || '')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2010-\u2015]/g, '-')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    const buildPdfChunkCandidates = (text) => {
        const normalized = normalizePdfSearchText(text);
        if (!normalized) return [];

        const candidates = new Set([normalized]);
        const words = normalized.split(' ').filter(Boolean);
        const windowSizes = [120, 90, 70, 50, 35, 24, 16];
        windowSizes.forEach((windowSize) => {
            if (words.length < windowSize) return;
            const step = Math.max(8, Math.floor(windowSize / 3));
            for (let start = 0; start <= words.length - windowSize; start += step) {
                candidates.add(words.slice(start, start + windowSize).join(' '));
            }
            candidates.add(words.slice(words.length - windowSize).join(' '));
        });

        return Array.from(candidates)
            .filter((candidate) => candidate.length >= 30)
            .sort((left, right) => right.length - left.length);
    };

    const buildPdfWordRanges = (text) => {
        const ranges = [];
        const matcher = /\S+/g;
        let match;
        while ((match = matcher.exec(text)) !== null) {
            ranges.push({ word: match[0], start: match.index, end: match.index + match[0].length });
        }
        return ranges;
    };

    const buildDetailedPdfChunkCandidates = (text) => {
        const normalized = normalizePdfSearchText(text);
        if (!normalized) return [];

        const words = buildPdfWordRanges(normalized);
        if (!words.length) return [];

        const candidates = new Map();
        const addCandidate = (wordStart, wordEnd, source) => {
            if (wordStart < 0 || wordEnd > words.length || wordStart >= wordEnd) return;
            const start = words[wordStart].start;
            const end = words[wordEnd - 1].end;
            const value = normalized.slice(start, end).trim();
            if (value.length < 30) return;
            const key = `${start}:${end}`;
            if (!candidates.has(key)) {
                candidates.set(key, { text: value, start, end, wordStart, wordEnd, source });
            }
        };

        addCandidate(0, words.length, 'full');
        [160, 120, 90, 70, 50, 35, 24, 16].forEach((windowSize) => {
            if (words.length < windowSize) return;
            const step = Math.max(6, Math.floor(windowSize / 4));
            for (let start = 0; start <= words.length - windowSize; start += step) {
                addCandidate(start, start + windowSize, start === 0 ? 'prefix' : 'window');
            }
            addCandidate(words.length - windowSize, words.length, 'suffix');
        });

        return Array.from(candidates.values())
            .sort((left, right) => {
                if (left.source === 'full') return -1;
                if (right.source === 'full') return 1;
                return right.text.length - left.text.length;
            });
    };

    const buildPdfBoundaryCandidates = (text, side) => {
        const normalized = normalizePdfSearchText(text);
        if (!normalized) return [];

        const words = normalized.split(' ').filter(Boolean);
        const windowSizes = [120, 90, 70, 50, 35, 24, 16, 10, 6];
        const candidates = new Set();
        windowSizes.forEach((windowSize) => {
            if (words.length < windowSize) return;
            const selectedWords = side === 'suffix'
                ? words.slice(words.length - windowSize)
                : words.slice(0, windowSize);
            candidates.add(selectedWords.join(' '));
        });
        candidates.add(normalized);

        return Array.from(candidates)
            .filter((candidate) => candidate.length >= 20)
            .sort((left, right) => right.length - left.length);
    };

    const findAllTextMatches = (documentText, candidate) => {
        const matches = [];
        if (!candidate) return matches;
        let offset = 0;
        while (offset < documentText.length) {
            const index = documentText.indexOf(candidate, offset);
            if (index === -1) break;
            matches.push({ start: index, end: index + candidate.length, score: candidate.length });
            offset = index + Math.max(1, candidate.length);
        }
        return matches;
    };

    const getPdfSimilarityTokens = (text) => {
        const stopWords = new Set(['the', 'and', 'for', 'that', 'this', 'with', 'from', 'are', 'was', 'were', 'have', 'has', 'had', 'not', 'but', 'you', 'your', 'their', 'there', 'which', 'would', 'could', 'should', 'into', 'than', 'then']);
        return normalizePdfSearchText(text)
            .split(' ')
            .map((word) => word.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''))
            .filter((word) => word.length > 2 && !stopWords.has(word));
    };

    const scorePdfTextSimilarity = (leftText, rightText) => {
        const leftTokens = getPdfSimilarityTokens(leftText);
        const rightTokens = getPdfSimilarityTokens(rightText);
        if (!leftTokens.length || !rightTokens.length) return 0;

        const leftCounts = new Map();
        leftTokens.forEach((token) => leftCounts.set(token, (leftCounts.get(token) || 0) + 1));

        let overlap = 0;
        rightTokens.forEach((token) => {
            const count = leftCounts.get(token) || 0;
            if (count <= 0) return;
            overlap += 1;
            leftCounts.set(token, count - 1);
        });

        return (2 * overlap) / (leftTokens.length + rightTokens.length);
    };

    const buildPdfAlignmentDebug = (documentText, targetText, textMatch, methodOverride = null) => {
        if (!textMatch) return null;
        const selectedText = documentText.slice(Math.max(0, textMatch.start), Math.max(0, textMatch.end));
        return {
            method: methodOverride || textMatch.method || (textMatch.anchored ? 'exact-context' : textMatch.boundaryAnchored ? 'boundary' : textMatch.approximate ? 'approximate' : 'exact'),
            confidence: Number.isFinite(textMatch.confidence) ? textMatch.confidence : scorePdfTextSimilarity(selectedText, targetText),
            selectedLength: selectedText.length,
            targetLength: targetText.length,
            expectedPages: textMatch.expectedPages || null,
            selectedPreview: selectedText.slice(0, 220),
            targetPreview: targetText.slice(0, 220),
        };
    };

    const getExpectedPdfChunkPageWindow = (chunkTarget, pageCount) => {
        const chunkIndex = Number(chunkTarget?.chunk_index);
        const chunkCount = Number(chunkTarget?.chunk_count);
        if (!Number.isFinite(chunkIndex) || !Number.isFinite(chunkCount) || chunkCount <= 1 || pageCount <= 0) {
            return null;
        }

        const centerPage = Math.max(1, Math.min(pageCount, Math.round(((chunkIndex + 0.5) / chunkCount) * pageCount)));
        const radius = Math.max(2, Math.ceil(pageCount / Math.max(chunkCount, 1)) + 2);
        return {
            startPage: Math.max(1, centerPage - radius),
            endPage: Math.min(pageCount, centerPage + radius),
            centerPage,
        };
    };

    const findPdfChunkMatch = (textItems, chunkText) => {
        const candidates = buildPdfChunkCandidates(chunkText);
        if (!candidates.length) return null;

        let normalizedPageText = '';
        const itemRanges = [];
        let textSpanIndex = 0;
        textItems.forEach((item, index) => {
            const itemText = normalizePdfSearchText(item?.str || '');
            if (!itemText) return;
            if (normalizedPageText) {
                normalizedPageText += ' ';
            }
            const start = normalizedPageText.length;
            normalizedPageText += itemText;
            itemRanges.push({ index, textSpanIndex, start, end: normalizedPageText.length });
            textSpanIndex += 1;
        });

        for (const candidate of candidates) {
            const matchStart = normalizedPageText.indexOf(candidate);
            if (matchStart === -1) continue;
            const matchEnd = matchStart + candidate.length;
            const itemIndexes = itemRanges
                .filter((range) => range.end >= matchStart && range.start <= matchEnd)
                .map((range) => range.textSpanIndex);
            if (itemIndexes.length > 0) {
                return { itemIndexes, score: candidate.length };
            }
        }

        return null;
    };

    const chooseBestPdfTextMatch = (documentText, targetText, preferredOffset = null) => {
        const normalizedTarget = normalizePdfSearchText(targetText);
        if (!normalizedTarget) return null;

        const matches = [];
        let searchOffset = 0;
        while (searchOffset < documentText.length) {
            const index = documentText.indexOf(normalizedTarget, searchOffset);
            if (index === -1) break;
            matches.push({ start: index, end: index + normalizedTarget.length, score: normalizedTarget.length, confidence: 1, method: 'exact' });
            searchOffset = index + Math.max(1, normalizedTarget.length);
        }

        if (matches.length > 0) {
            if (preferredOffset !== null && Number.isFinite(preferredOffset)) {
                matches.sort((left, right) => Math.abs(left.start - preferredOffset) - Math.abs(right.start - preferredOffset));
            }
            return matches[0];
        }

        let bestProjectedMatch = null;
        buildDetailedPdfChunkCandidates(normalizedTarget).forEach((candidate) => {
            findAllTextMatches(documentText, candidate.text).forEach((candidateMatch) => {
                const projectedStart = Math.max(0, candidateMatch.start - candidate.start);
                const projectedEnd = Math.min(documentText.length, projectedStart + normalizedTarget.length);
                if (projectedEnd <= projectedStart) return;

                const selectedText = documentText.slice(projectedStart, projectedEnd);
                const confidence = scorePdfTextSimilarity(selectedText, normalizedTarget);
                const lengthRatio = Math.min(selectedText.length, normalizedTarget.length) / Math.max(selectedText.length, normalizedTarget.length);
                const offsetPenalty = preferredOffset !== null && Number.isFinite(preferredOffset)
                    ? Math.min(0.15, Math.abs(projectedStart - preferredOffset) / Math.max(documentText.length, 1))
                    : 0;
                const sourceBoost = candidate.source === 'prefix' ? 0.04 : candidate.source === 'full' ? 0.08 : 0;
                const score = (confidence * 1000) + (lengthRatio * 140) + (candidate.text.length * 0.25) + sourceBoost - (offsetPenalty * 1000);

                if (!bestProjectedMatch || score > bestProjectedMatch.score) {
                    bestProjectedMatch = {
                        start: projectedStart,
                        end: projectedEnd,
                        score,
                        confidence,
                        approximate: true,
                        method: `projected-${candidate.source}`,
                        candidateLength: candidate.text.length,
                    };
                }
            });
        });

        if (bestProjectedMatch && bestProjectedMatch.confidence >= 0.48) {
            return bestProjectedMatch;
        }

        let bestCandidateOnlyMatch = null;
        buildDetailedPdfChunkCandidates(normalizedTarget).forEach((candidate) => {
            if (candidate.source !== 'prefix' && candidate.source !== 'full') return;
            findAllTextMatches(documentText, candidate.text).forEach((candidateMatch) => {
                const confidence = scorePdfTextSimilarity(candidate.text, normalizedTarget);
                const score = (confidence * 600) + candidate.text.length;
                if (!bestCandidateOnlyMatch || score > bestCandidateOnlyMatch.score) {
                    bestCandidateOnlyMatch = {
                        start: candidateMatch.start,
                        end: candidateMatch.end,
                        score,
                        confidence,
                        approximate: true,
                        method: `candidate-${candidate.source}`,
                        candidateLength: candidate.text.length,
                    };
                }
            });
        });

        if (bestCandidateOnlyMatch && bestCandidateOnlyMatch.confidence >= 0.35) {
            return bestCandidateOnlyMatch;
        }

        if (bestProjectedMatch) {
            return {
                ...bestProjectedMatch,
                rejected: true,
            };
        }

        if (bestCandidateOnlyMatch) {
            return {
                ...bestCandidateOnlyMatch,
                rejected: true,
            };
        }

        return null;
    };

    const isPdfTextMatchConfident = (documentText, normalizedChunkText, textMatch, minimumConfidence = 0.45) => {
        if (!textMatch) return false;
        if (textMatch.confidence === 1 || textMatch.anchored) return true;
        const selectedText = documentText.slice(Math.max(0, textMatch.start), Math.max(0, textMatch.end));
        const confidence = Number.isFinite(textMatch.confidence)
            ? textMatch.confidence
            : scorePdfTextSimilarity(selectedText, normalizedChunkText);
        textMatch.confidence = confidence;
        return confidence >= minimumConfidence;
    };

    const buildPdfChunkAlignment = async (pdf, chunkTarget, isCancelled) => {
        const chunkText = chunkTarget?.chunk_text;
        const normalizedChunkText = normalizePdfSearchText(chunkText);
        if (!normalizedChunkText) return null;

        const coordinatePages = Array.isArray(chunkTarget?.coordinate_pages) ? chunkTarget.coordinate_pages : [];
        if (coordinatePages.length > 0) {
            const pageBoxes = new Map();
            coordinatePages.forEach((page) => {
                const pageNumber = Number(page?.page_number);
                const width = Number(page?.width);
                const height = Number(page?.height);
                const boxes = Array.isArray(page?.boxes) ? page.boxes : [];
                const validBoxes = boxes
                    .map((box) => ({ ...box, bbox: box?.bbox || null }))
                    .filter((box) => {
                        const bbox = box.bbox;
                        return Number.isFinite(pageNumber)
                            && bbox
                            && Number.isFinite(Number(bbox.x0))
                            && Number.isFinite(Number(bbox.y0))
                            && Number.isFinite(Number(bbox.x1))
                            && Number.isFinite(Number(bbox.y1));
                    });
                if (validBoxes.length > 0) {
                    pageBoxes.set(pageNumber, {
                        width: Number.isFinite(width) && width > 0 ? width : null,
                        height: Number.isFinite(height) && height > 0 ? height : null,
                        boxes: validBoxes,
                    });
                }
            });

            const pages = Array.from(pageBoxes.keys()).sort((left, right) => left - right);
            if (pages.length > 0) {
                return {
                    pages,
                    firstPage: pages[0],
                    pageBoxes,
                    approximate: false,
                    anchored: true,
                    score: 1,
                    debug: {
                        method: 'v2-coordinates',
                        confidence: 1,
                        selectedLength: normalizedChunkText.length,
                        targetLength: normalizedChunkText.length,
                        expectedPages: `${pages[0]}-${pages[pages.length - 1]}`,
                        selectedPreview: normalizedChunkText.slice(0, 220),
                        targetPreview: normalizedChunkText.slice(0, 220),
                    },
                };
            }
        }

        let documentText = '';
        const textRanges = [];
        const pageTextRanges = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
            if (isCancelled()) return null;
            const page = await pdf.getPage(pageNumber);
            const textContent = await page.getTextContent();
            const pageStart = documentText.length;
            let pageSpanIndex = 0;
            (textContent.items || []).forEach((item) => {
                const itemText = normalizePdfSearchText(item?.str || '');
                if (!itemText) return;
                if (documentText) {
                    documentText += ' ';
                }
                const start = documentText.length;
                documentText += itemText;
                textRanges.push({
                    pageNumber,
                    spanIndex: pageSpanIndex,
                    start,
                    end: documentText.length,
                });
                pageSpanIndex += 1;
            });
            pageTextRanges.push({ pageNumber, start: pageStart, end: documentText.length });
            page.cleanup?.();
        }

        if (!documentText || textRanges.length === 0) return null;
        const expectedPageWindow = getExpectedPdfChunkPageWindow(chunkTarget, pdf.numPages);
        const expectedTextWindow = expectedPageWindow
            ? pageTextRanges.reduce((window, pageRange) => {
                if (pageRange.pageNumber < expectedPageWindow.startPage || pageRange.pageNumber > expectedPageWindow.endPage) {
                    return window;
                }
                return {
                    start: Math.min(window.start, pageRange.start),
                    end: Math.max(window.end, pageRange.end),
                };
            }, { start: Infinity, end: -Infinity })
            : null;
        const hasExpectedTextWindow = expectedTextWindow && Number.isFinite(expectedTextWindow.start) && expectedTextWindow.end > expectedTextWindow.start;
        const expectedPagesLabel = expectedPageWindow ? `${expectedPageWindow.startPage}-${expectedPageWindow.endPage}` : null;

        const contextChunks = Array.isArray(chunkTarget?.context_chunks) ? chunkTarget.context_chunks : [];
        const matchIndex = contextChunks.findIndex((contextChunk) => contextChunk?.is_match);
        const beforeText = matchIndex > 0
            ? contextChunks.slice(0, matchIndex).map((contextChunk) => contextChunk?.chunk_text || '').join(' ').slice(-1200)
            : '';
        const afterText = matchIndex >= 0
            ? contextChunks.slice(matchIndex + 1).map((contextChunk) => contextChunk?.chunk_text || '').join(' ').slice(0, 1200)
            : '';
        const normalizedBefore = normalizePdfSearchText(beforeText);
        const normalizedAfter = normalizePdfSearchText(afterText);

        let textMatch = null;
        const anchorAttempts = [
            { prefix: normalizedBefore, full: [normalizedBefore, normalizedChunkText, normalizedAfter].filter(Boolean).join(' ') },
            { prefix: normalizedBefore, full: [normalizedBefore, normalizedChunkText].filter(Boolean).join(' ') },
            { prefix: '', full: [normalizedChunkText, normalizedAfter].filter(Boolean).join(' ') },
        ].filter((attempt) => attempt.full.length > normalizedChunkText.length + 20);

        for (const attempt of anchorAttempts) {
            const anchorIndex = documentText.indexOf(attempt.full);
            if (anchorIndex === -1) continue;
            const matchStart = anchorIndex + (attempt.prefix ? attempt.prefix.length + 1 : 0);
            textMatch = {
                start: matchStart,
                end: matchStart + normalizedChunkText.length,
                score: attempt.full.length,
                anchored: true,
            };
            break;
        }

        const allowBoundaryAnchoring = false;
        if (allowBoundaryAnchoring && !textMatch && normalizedBefore && normalizedAfter) {
            const beforeMatches = buildPdfBoundaryCandidates(normalizedBefore, 'suffix')
                .flatMap((candidate) => findAllTextMatches(documentText, candidate).map((match) => ({ ...match, boundary: 'before' })));
            const afterMatches = buildPdfBoundaryCandidates(normalizedAfter, 'prefix')
                .flatMap((candidate) => findAllTextMatches(documentText, candidate).map((match) => ({ ...match, boundary: 'after' })));

            let bestBoundaryMatch = null;
            beforeMatches.forEach((beforeMatch) => {
                afterMatches.forEach((afterMatch) => {
                    if (afterMatch.start <= beforeMatch.end) return;
                    const rangeStart = beforeMatch.end;
                    const rangeEnd = afterMatch.start;
                    const rangeLength = rangeEnd - rangeStart;
                    if (rangeLength < normalizedChunkText.length * 0.35 || rangeLength > normalizedChunkText.length * 1.9) return;
                    const selectedText = documentText.slice(rangeStart, rangeEnd);
                    const confidence = scorePdfTextSimilarity(selectedText, normalizedChunkText);
                    if (confidence < 0.48) return;
                    const lengthDelta = Math.abs(rangeLength - normalizedChunkText.length);
                    const score = (confidence * 1000) + beforeMatch.score + afterMatch.score - lengthDelta;
                    if (!bestBoundaryMatch || score > bestBoundaryMatch.score) {
                        bestBoundaryMatch = {
                            start: rangeStart,
                            end: rangeEnd,
                            score,
                            confidence,
                            method: 'boundary',
                            boundaryAnchored: true,
                        };
                    }
                });
            });

            if (bestBoundaryMatch) {
                textMatch = bestBoundaryMatch;
            }
        }

        if (!textMatch && hasExpectedTextWindow) {
            const windowText = documentText.slice(expectedTextWindow.start, expectedTextWindow.end);
            const windowMatch = chooseBestPdfTextMatch(windowText, normalizedChunkText);
            if (windowMatch && !windowMatch.rejected) {
                textMatch = {
                    ...windowMatch,
                    start: windowMatch.start + expectedTextWindow.start,
                    end: windowMatch.end + expectedTextWindow.start,
                    method: `${windowMatch.method || 'matched'}-page-window`,
                    expectedPages: expectedPagesLabel,
                };
            }
        }

        if (!textMatch) {
            textMatch = chooseBestPdfTextMatch(documentText, normalizedChunkText, hasExpectedTextWindow ? expectedTextWindow.start : null);
            if (textMatch && expectedPagesLabel) {
                textMatch.expectedPages = expectedPagesLabel;
            }
        }

        if (!textMatch) return null;

        if (normalizedAfter && textMatch.end > textMatch.start) {
            const afterSearchStart = Math.max(textMatch.start, Math.floor(textMatch.start + (normalizedChunkText.length * 0.55)));
            const afterSearchEnd = Math.min(documentText.length, Math.ceil(textMatch.end + (normalizedChunkText.length * 0.18)));
            const afterSearchText = documentText.slice(afterSearchStart, afterSearchEnd);
            let bestAfterBoundary = null;
            buildPdfBoundaryCandidates(normalizedAfter, 'prefix').forEach((candidate) => {
                const relativeIndex = afterSearchText.indexOf(candidate);
                if (relativeIndex === -1) return;
                const absoluteIndex = afterSearchStart + relativeIndex;
                if (absoluteIndex <= textMatch.start) return;
                const trimmedLength = absoluteIndex - textMatch.start;
                if (trimmedLength < normalizedChunkText.length * 0.62 || trimmedLength > normalizedChunkText.length * 1.15) return;
                const lengthDelta = Math.abs(trimmedLength - normalizedChunkText.length);
                const score = candidate.length - lengthDelta;
                if (!bestAfterBoundary || score > bestAfterBoundary.score) {
                    bestAfterBoundary = { index: absoluteIndex, score, candidateLength: candidate.length };
                }
            });

            if (bestAfterBoundary && bestAfterBoundary.index < textMatch.end) {
                textMatch = {
                    ...textMatch,
                    end: bestAfterBoundary.index,
                    method: `${textMatch.method || 'matched'}-trim-after`,
                    trimmedAfter: true,
                };
            }
        }

        const debug = buildPdfAlignmentDebug(documentText, normalizedChunkText, textMatch);
        if (textMatch.rejected || !isPdfTextMatchConfident(documentText, normalizedChunkText, textMatch)) {
            return {
                rejected: true,
                debug,
            };
        }

        const pageSpanIndexes = new Map();
        textRanges.forEach((range) => {
            if (range.end <= textMatch.start || range.start >= textMatch.end) return;
            if (!pageSpanIndexes.has(range.pageNumber)) {
                pageSpanIndexes.set(range.pageNumber, new Set());
            }
            pageSpanIndexes.get(range.pageNumber).add(range.spanIndex);
        });

        const pages = Array.from(pageSpanIndexes.keys()).sort((left, right) => left - right);
        if (pages.length === 0) return null;

        return {
            pages,
            firstPage: pages[0],
            pageSpanIndexes,
            approximate: !!textMatch.approximate,
            anchored: !!textMatch.anchored,
            boundaryAnchored: !!textMatch.boundaryAnchored,
            score: textMatch.score,
            debug,
        };
    };

    const drawPdfChunkOverlay = (pageWrapper, textLayerDiv, matchedSpans) => {
        pageWrapper.querySelectorAll('.pdf-chunk-highlight-overlay').forEach((node) => node.remove());
        if (!matchedSpans.length) return;

        const pageRect = pageWrapper.getBoundingClientRect();
        const lineRects = [];
        matchedSpans.forEach((span) => {
            const rect = span.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            const relativeRect = {
                left: rect.left - pageRect.left,
                right: rect.right - pageRect.left,
                top: rect.top - pageRect.top,
                bottom: rect.bottom - pageRect.top,
            };
            const existingLine = lineRects.find((line) => Math.abs(line.top - relativeRect.top) < 6);
            if (existingLine) {
                existingLine.left = Math.min(existingLine.left, relativeRect.left);
                existingLine.right = Math.max(existingLine.right, relativeRect.right);
                existingLine.top = Math.min(existingLine.top, relativeRect.top);
                existingLine.bottom = Math.max(existingLine.bottom, relativeRect.bottom);
            } else {
                lineRects.push(relativeRect);
            }
        });

        if (!lineRects.length) return;

        const overlay = document.createElement('div');
        overlay.className = 'pdf-chunk-highlight-overlay';
        lineRects.forEach((line) => {
            const box = document.createElement('div');
            box.className = 'pdf-chunk-highlight-box';
            box.style.left = `${Math.max(0, line.left - 5)}px`;
            box.style.top = `${Math.max(0, line.top - 3)}px`;
            box.style.width = `${Math.max(8, line.right - line.left + 10)}px`;
            box.style.height = `${Math.max(8, line.bottom - line.top + 6)}px`;
            overlay.appendChild(box);
        });
        pageWrapper.insertBefore(overlay, textLayerDiv);
    };

    const drawPdfCoordinateOverlay = (pageWrapper, textLayerDiv, pageBoxData) => {
        pageWrapper.querySelectorAll('.pdf-chunk-highlight-overlay').forEach((node) => node.remove());
        const boxes = Array.isArray(pageBoxData?.boxes) ? pageBoxData.boxes : [];
        if (!boxes.length) return;

        const pageWidth = Number(pageBoxData?.width) || pageWrapper.clientWidth;
        const pageHeight = Number(pageBoxData?.height) || pageWrapper.clientHeight;
        const xScale = pageWrapper.clientWidth / Math.max(1, pageWidth);
        const yScale = pageWrapper.clientHeight / Math.max(1, pageHeight);
        const overlay = document.createElement('div');
        overlay.className = 'pdf-chunk-highlight-overlay';

        boxes.forEach((boxData) => {
            const bbox = boxData?.bbox;
            if (!bbox) return;
            const x0 = Number(bbox.x0);
            const y0 = Number(bbox.y0);
            const x1 = Number(bbox.x1);
            const y1 = Number(bbox.y1);
            if (![x0, y0, x1, y1].every(Number.isFinite) || x1 <= x0 || y1 <= y0) return;

            const box = document.createElement('div');
            box.className = 'pdf-chunk-highlight-box';
            box.style.left = `${Math.max(0, x0 * xScale - 5)}px`;
            box.style.top = `${Math.max(0, y0 * yScale - 3)}px`;
            box.style.width = `${Math.max(8, (x1 - x0) * xScale + 10)}px`;
            box.style.height = `${Math.max(8, (y1 - y0) * yScale + 6)}px`;
            overlay.appendChild(box);
        });

        if (overlay.childElementCount > 0) {
            pageWrapper.insertBefore(overlay, textLayerDiv || pageWrapper.firstChild);
        }
    };

    const applyPdfChunkHighlight = (pageWrapper, textLayerDiv, pageNumber) => {
        if (!showChunkHighlightsRef.current) {
            pageWrapper.querySelectorAll('.pdf-chunk-highlight-overlay').forEach((node) => node.remove());
            return false;
        }

        const alignment = pdfChunkAlignmentRef.current;
        const pageBoxData = alignment?.pageBoxes?.get(pageNumber);
        if (pageBoxData) {
            drawPdfCoordinateOverlay(pageWrapper, textLayerDiv, pageBoxData);
            if (pdfHighlightAppliedRef.current !== alignment.firstPage) {
                pdfHighlightAppliedRef.current = alignment.firstPage;
                setPdfHighlightState({
                    status: 'found',
                    pageNumber: alignment.firstPage,
                    message: `Highlighted stored v2 chunk coordinates starting on page ${alignment.firstPage}.`,
                    debug: alignment.debug || null,
                });
                window.requestAnimationFrame(() => {
                    pageWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            }
            return true;
        }

        const pageIndexes = alignment?.pageSpanIndexes?.get(pageNumber);
        if (!pageIndexes || !textLayerDiv) return false;

        const textSpans = Array.from(textLayerDiv.querySelectorAll('span'))
            .filter((span) => normalizePdfSearchText(span.textContent).length > 0);
        const matchedSpans = textSpans.filter((span, index) => pageIndexes.has(index));
        drawPdfChunkOverlay(pageWrapper, textLayerDiv, matchedSpans);

        if (pdfHighlightAppliedRef.current !== alignment.firstPage) {
            pdfHighlightAppliedRef.current = alignment.firstPage;
            setPdfHighlightState({
                status: 'found',
                pageNumber: alignment.firstPage,
                message: alignment.approximate
                    ? `Highlighted best aligned chunk range starting on page ${alignment.firstPage} (${alignment.debug?.method || 'matched'}, ${Math.round((alignment.debug?.confidence || 0) * 100)}% confidence).`
                    : `Highlighted aligned chunk range starting on page ${alignment.firstPage} (${alignment.debug?.method || 'matched'}, ${Math.round((alignment.debug?.confidence || 0) * 100)}% confidence).`,
                debug: alignment.debug || null,
            });
            window.requestAnimationFrame(() => {
                pageWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }

        return true;
    };

    const getRedactionDraftStorageKey = useCallback(() => {
        if (!fileId) return null;
        return `casedb:file-redaction-draft:${fileId}`;
    }, [fileId]);

    const removeRedactionSelectionGroup = useCallback((selectionGroupId, boxId = null) => {
        if (!selectionGroupId && !boxId) return;
        setRedactionBoxes((current) => current.filter((box) => {
            if (selectionGroupId && box.selectionGroupId) {
                return box.selectionGroupId !== selectionGroupId;
            }
            if (selectionGroupId && !box.selectionGroupId) {
                return box.id !== selectionGroupId;
            }
            return box.id !== boxId;
        }));
    }, []);

    const handleCompleteRedaction = useCallback(() => {
        if (!canUseRedactionMode || redactionBoxes.length === 0) return;

        const storageKey = getRedactionDraftStorageKey();
        setRedactionDraftState((prev) => ({ ...prev, isSaving: true, error: null }));

        const run = async () => {
            const now = new Date().toISOString();

            try {
                await ensureCaseDbCsrfToken();
                const response = await fetch('https://api.casedb.co/redact-pdf.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: withCsrfHeaders({
                        'Content-Type': 'application/json'
                    }, 'POST'),
                    body: JSON.stringify({
                        file_id: Number(file?.id || fileId),
                        boxes: redactionBoxes,
                    })
                });

                const responseText = await response.text();
                let data = null;
                try {
                    data = JSON.parse(responseText);
                } catch (jsonError) {
                    throw new Error(responseText?.trim() || 'Unable to save redaction');
                }

                if (!response.ok || !data?.success || !data?.redacted_file?.signed_url) {
                    throw new Error(data?.message || 'Unable to save redaction');
                }

                const redactedFile = data.redacted_file;
                setFile((current) => ({
                    ...current,
                    id: redactedFile.id || current?.id,
                    file_name: redactedFile.file_name || current?.file_name,
                    file_path: redactedFile.file_path || current?.file_path,
                    url: redactedFile.signed_url,
                    redacted_from_file_id: data?.source_file_id || current?.redacted_from_file_id,
                }));
                setRedactionBoxes([]);

                if (storageKey) {
                    window.localStorage.removeItem(storageKey);
                }

                setRedactionDraftState({
                    isSaving: false,
                    error: null,
                    lastSavedAt: now,
                });
            } catch (error) {
                console.error('Unable to save redaction', error);

                if (storageKey) {
                    try {
                        window.localStorage.setItem(storageKey, JSON.stringify({
                            file_id: fileId,
                            file_name: file?.file_name || '',
                            file_path: file?.file_path || file?.key || file?.path || file?.name || '',
                            boxes: redactionBoxes,
                            saved_at: now,
                        }));
                    } catch (draftError) {
                        console.warn('Unable to save redaction draft backup', draftError);
                    }
                }

                setRedactionDraftState({
                    isSaving: false,
                    error: error?.message || 'Unable to save redaction. Draft kept locally.',
                    lastSavedAt: null,
                });
            }
        };

        run();
    }, [canUseRedactionMode, redactionBoxes, getRedactionDraftStorageKey, fileId, file]);

    const drawPdfRedactionOverlays = useCallback(() => {
        const container = pdfCanvasContainerRef.current;
        if (!container) return;

        container.querySelectorAll('.pdf-redaction-overlay').forEach((node) => node.remove());
        if (!canUseRedactionMode || redactionBoxes.length === 0) return;

        const boxesByPage = redactionBoxes.reduce((pages, box) => {
            const pageNumber = Number(box.pageNumber || 0);
            if (!pageNumber) return pages;
            if (!pages.has(pageNumber)) pages.set(pageNumber, []);
            pages.get(pageNumber).push(box);
            return pages;
        }, new Map());

        boxesByPage.forEach((pageBoxes, pageNumber) => {
            const pageWrapper = container.querySelector(`.pdf-page[data-page-number="${pageNumber}"]`);
            if (!pageWrapper) return;

            const overlay = document.createElement('div');
            overlay.className = 'pdf-redaction-overlay';
            pageBoxes.forEach((boxData) => {
                const box = document.createElement('div');
                box.className = 'pdf-redaction-box';
                box.dataset.boxId = String(boxData.id || '');
                box.dataset.selectionGroupId = String(boxData.selectionGroupId || boxData.id || '');
                box.setAttribute('role', 'button');
                box.setAttribute('tabindex', '0');
                box.setAttribute('aria-label', 'Remove selected redaction');
                box.title = 'Click to remove this selected redaction group';
                box.style.left = `${Math.max(0, boxData.left)}px`;
                box.style.top = `${Math.max(0, boxData.top)}px`;
                box.style.width = `${Math.max(4, boxData.width)}px`;
                box.style.height = `${Math.max(4, boxData.height)}px`;
                box.addEventListener('click', (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const selectionGroupId = boxData.selectionGroupId || boxData.id || null;
                    removeRedactionSelectionGroup(selectionGroupId, boxData.id || null);
                });
                box.addEventListener('keydown', (event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    event.stopPropagation();
                    const selectionGroupId = boxData.selectionGroupId || boxData.id || null;
                    removeRedactionSelectionGroup(selectionGroupId, boxData.id || null);
                });
                overlay.appendChild(box);
            });

            pageWrapper.appendChild(overlay);
        });
    }, [canUseRedactionMode, redactionBoxes, removeRedactionSelectionGroup]);

    const capturePdfRedactionSelection = useCallback((event) => {
        if (!canUseRedactionMode) return;
        if (event?.target?.closest?.('.pdf-redaction-box')) return;

        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

        const selectedText = selection.toString().trim();
        if (!selectedText) return;

        const selectionRects = Array.from(selection.getRangeAt(0).getClientRects()).filter((rect) => rect.width > 1 && rect.height > 1);
        if (!selectionRects.length) return;

        const container = pdfCanvasContainerRef.current;
        if (!container) return;

        const pageWrappers = Array.from(container.querySelectorAll('.pdf-page'));
        const capturedBoxes = [];
        const selectionGroupId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

        for (const pageWrapper of pageWrappers) {
            const pageRect = pageWrapper.getBoundingClientRect();
            const pageNumber = Number(pageWrapper.dataset.pageNumber || 0);
            if (!pageNumber) continue;

            for (const rect of selectionRects) {
                const left = Math.max(rect.left, pageRect.left);
                const right = Math.min(rect.right, pageRect.right);
                const top = Math.max(rect.top, pageRect.top);
                const bottom = Math.min(rect.bottom, pageRect.bottom);
                if (right <= left || bottom <= top) continue;

                capturedBoxes.push({
                    id: `${selectionGroupId}-${pageNumber}-${capturedBoxes.length}`,
                    selectionGroupId,
                    pageNumber,
                    left: left - pageRect.left,
                    top: top - pageRect.top,
                    width: right - left,
                    height: bottom - top,
                    pageWidth: pageWrapper.clientWidth,
                    pageHeight: pageWrapper.clientHeight,
                    text: selectedText
                });
            }
        }

        if (capturedBoxes.length > 0) {
            setRedactionBoxes((current) => [...current, ...capturedBoxes]);
            selection.removeAllRanges();
        }
    }, [canUseRedactionMode]);

    useEffect(() => {
        pdfHighlightAppliedRef.current = null;
        setPdfHighlightState({ status: shouldPdfHighlightChunk ? 'loading' : 'idle', pageNumber: null, message: '', debug: null });
        setForceNativePdfViewer(false);
    }, [fileId, targetRegistryId, targetChunkIndex, targetChunkToken, shouldPdfHighlightChunk]);

    useEffect(() => {
        showChunkHighlightsRef.current = showChunkHighlights;

        if (showChunkHighlights) {
            if (shouldPdfHighlightChunk && pdfChunkAlignmentRef.current) {
                pdfHighlightAppliedRef.current = null;
                window.requestAnimationFrame(() => {
                    document.querySelectorAll('.pdf-page[data-page-number]').forEach((pageWrapper) => {
                        const pageNumber = Number(pageWrapper.dataset.pageNumber || 0);
                        if (!pageNumber) return;
                        applyPdfChunkHighlight(pageWrapper, pageWrapper.querySelector('.textLayer'), pageNumber);
                    });
                });
            }
            return;
        }

        document.querySelectorAll('.pdf-chunk-highlight-overlay').forEach((node) => node.remove());
        pdfHighlightAppliedRef.current = null;
        if (shouldPdfHighlightChunk) {
            setPdfHighlightState({ status: 'hidden', pageNumber: null, message: 'Chunk highlights hidden.', debug: null });
        }
    }, [showChunkHighlights, shouldPdfHighlightChunk]);

    const readStoredChunkTarget = () => {
        if (!targetChunkToken) return null;
        try {
            const storageKey = `casedb:file-viewer-chunk:${targetChunkToken}`;
            const raw = window.localStorage.getItem(storageKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed?.chunk_text) return null;
            if (parsed.created_at && Date.now() - Number(parsed.created_at) > 60 * 60 * 1000) {
                window.localStorage.removeItem(storageKey);
                return null;
            }
            return parsed;
        } catch (error) {
            console.warn('Unable to read stored chunk target', error);
            return null;
        }
    };

    useEffect(() => {
        if (!shouldPdfHighlightChunk) {
            setChunkTargetState({ isLoading: false, error: null, data: null });
            return;
        }

        let cancelled = false;
        const fetchChunkTarget = async () => {
            const storedChunkTarget = readStoredChunkTarget();
            setChunkTargetState({ isLoading: true, error: null, data: storedChunkTarget });
            try {
                const params = new URLSearchParams();
                params.set('registry_id', targetRegistryId);
                params.set('chunk_index', targetChunkIndex);
                params.set('file_id', fileId);
                const response = await fetch(`https://api.casedb.co/file-viewer-chunk-context.php?${params.toString()}`, { credentials: 'include' });
                const responseText = await response.text();
                let data = null;
                try {
                    data = JSON.parse(responseText);
                } catch (jsonError) {
                    throw new Error(responseText?.trim() || 'Unable to load matched chunk');
                }
                if (!response.ok || !data?.success || !data?.chunk_target) {
                    throw new Error(data?.message || 'Unable to load matched chunk');
                }
                if (!cancelled) {
                    setChunkTargetState({ isLoading: false, error: null, data: data.chunk_target });
                }
            } catch (err) {
                if (!cancelled) {
                    const fallbackChunkTarget = storedChunkTarget || readStoredChunkTarget();
                    if (fallbackChunkTarget?.chunk_text) {
                        setChunkTargetState({ isLoading: false, error: null, data: fallbackChunkTarget });
                        setPdfHighlightState({ status: 'loading', pageNumber: null, message: 'Using chunk from search result handoff.' });
                    } else {
                        setChunkTargetState({ isLoading: false, error: err.message || 'Unable to load matched chunk', data: null });
                        setPdfHighlightState({ status: 'error', pageNumber: null, message: err.message || 'Unable to load matched chunk' });
                    }
                }
            }
        };

        fetchChunkTarget();
        return () => {
            cancelled = true;
        };
    }, [fileId, targetRegistryId, targetChunkIndex, targetChunkToken, shouldPdfHighlightChunk]);

    useEffect(() => {
        if (!shouldUseTemporaryPdfPreview || !file || fileExtension === 'pdf' || !canConvertToPdfPreview) {
            setTemporaryPdfState({ isLoading: false, error: null, url: null, key: null });
            return;
        }

        let cancelled = false;
        const convertTemporaryPdf = async () => {
            const fileKey = file.file_path || file.key || file.path || file.file_name;
            if (!fileKey) {
                setTemporaryPdfState({ isLoading: false, error: 'No file path available for PDF conversion', url: null, key: null });
                return;
            }

            setTemporaryPdfState({ isLoading: true, error: null, url: null, key: null });
            try {
                const response = await fetch('https://api.casedb.co/convert_to_pdf.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        file_key: fileKey,
                        case_id: file.case_id,
                        temporary: true,
                        ocr_pdf: false,
                    })
                });
                const data = await response.json();
                if (!response.ok || !data?.success || !data?.converted_file?.signed_url) {
                    throw new Error(data?.error || 'Unable to create temporary PDF preview');
                }
                if (!cancelled) {
                    setTemporaryPdfState({
                        isLoading: false,
                        error: null,
                        url: data.converted_file.signed_url,
                        key: data.converted_file.key || null
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    setTemporaryPdfState({ isLoading: false, error: err.message || 'Unable to create temporary PDF preview', url: null, key: null });
                    if (shouldPdfHighlightChunk) {
                        setPdfHighlightState({ status: 'error', pageNumber: null, message: err.message || 'Unable to create temporary PDF preview' });
                    }
                }
            }
        };

        convertTemporaryPdf();
        return () => {
            cancelled = true;
        };
    }, [file, fileExtension, canConvertToPdfPreview, shouldUseTemporaryPdfPreview, shouldPdfHighlightChunk]);

    useEffect(() => {
        const workerUrl = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs';
        pdfWorkerUrlRef.current = workerUrl;
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

        return () => {
            pdfWorkerUrlRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (viewerExtension !== "pdf") return;

        pdfCurrentPageRef.current = pdfCurrentPage;
    }, [viewerExtension, pdfCurrentPage]);

    useEffect(() => {
        if (viewerExtension !== "pdf") return;
        pdfVirtualizerRef.current?.updateVisiblePages?.(pdfCurrentPage);
    }, [viewerExtension, pdfCurrentPage]);

    useEffect(() => {
        if (viewerExtension !== "pdf") return;

        setPdfDisplayedScale((previousScale) => {
            if (Math.abs(previousScale - pdfRenderScale) < 0.001) {
                return previousScale;
            }
            return pdfRenderScale;
        });
    }, [activePdfUrl, viewerExtension, pdfRenderScale]);

    useEffect(() => {
        if (viewerExtension !== "pdf") return;

        const timeoutId = window.setTimeout(() => {
            setPdfRenderScale((previousScale) => {
                if (Math.abs(previousScale - pdfScale) < 0.001) {
                    return previousScale;
                }
                return pdfScale;
            });
        }, 180);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [viewerExtension, pdfScale]);

    useEffect(() => {
        if (viewerExtension !== "pdf" || !activePdfUrl) return;

        const container = pdfCanvasContainerRef.current;
        const thumbsContainer = pdfThumbsContainerRef.current;
        if (!container || !thumbsContainer) return;

        let cancelled = false;
        let loadingTask = null;
        let backgroundThumbnailPromise = null;
        const renderedPageSet = new Set();

        const waitForContainerSize = async (el, maxFrames = 12) => {
            for (let i = 0; i < maxFrames; i += 1) {
                const rect = el.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) return rect;
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }
            return el.getBoundingClientRect();
        };

        const yieldToBrowser = () => new Promise((resolve) => window.setTimeout(resolve, 0));

        const renderPdf = async () => {
            container.replaceChildren();
            thumbsContainer.replaceChildren();
            lastActiveThumbRef.current = null;
            pdfVirtualizerRef.current = null;
            setPdfRenderState({ isRendering: true, error: null });
            setPdfProgress({
                isLoadingDocument: true,
                downloadPercent: 0,
                renderedPages: 0,
                totalPages: 0,
                renderedThumbs: 0,
                totalThumbs: 0,
                mode: 'standard',
                isWindowRendering: false,
                windowRenderedPages: 0,
                windowTotalPages: 0,
                windowStartPage: 0,
                windowEndPage: 0,
                windowAnchorPage: 0
            });

            try {
                const scrollAnchor = capturePdfScrollAnchor();
                loadingTask = pdfjsLib.getDocument({
                    url: activePdfUrl,
                    enableXfa: true,
                    wasmUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/wasm/',
                    cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/cmaps/',
                    cMapPacked: true,
                    standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/standard_fonts/'
                });
                loadingTask.onProgress = (progressData) => {
                    if (cancelled) return;
                    const loaded = Number(progressData?.loaded || 0);
                    const total = Number(progressData?.total || 0);
                    const percent = total > 0 ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100))) : 0;
                    setPdfProgress((prev) => ({
                        ...prev,
                        isLoadingDocument: true,
                        downloadPercent: percent
                    }));
                };
                const pdf = await loadingTask.promise;
                if (cancelled) return;

                if (!pdf.numPages || pdf.numPages < 1) {
                    throw new Error('PDF has no pages to render');
                }

                setPageCount(pdf.numPages);

                const optimizedMode = pdf.numPages >= PDF_OPTIMIZED_MODE_THRESHOLD;
                const enableTextLayer = true;
                const thumbnailTotal = pdf.numPages;
                setPdfProgress({
                    isLoadingDocument: false,
                    downloadPercent: 100,
                    renderedPages: 0,
                    totalPages: pdf.numPages,
                    renderedThumbs: 0,
                    totalThumbs: thumbnailTotal,
                    mode: optimizedMode ? 'optimized' : 'standard',
                    isWindowRendering: false,
                    windowRenderedPages: 0,
                    windowTotalPages: 0,
                    windowStartPage: 0,
                    windowEndPage: 0,
                    windowAnchorPage: 0
                });

                let targetPageAfterRender = Math.max(1, Math.min(pdfCurrentPageRef.current || 1, pdf.numPages));
                pdfChunkAlignmentRef.current = null;
                if (showChunkHighlightsRef.current && shouldPdfHighlightChunk && chunkTargetState.data?.chunk_text) {
                    setPdfHighlightState({ status: 'locating', pageNumber: null, message: 'Aligning matched chunk against full PDF text...' });
                    const alignment = await buildPdfChunkAlignment(pdf, chunkTargetState.data, () => cancelled);
                    if (alignment?.rejected) {
                        pdfChunkAlignmentRef.current = null;
                        setPdfHighlightState({
                            status: 'low-confidence',
                            pageNumber: null,
                            message: 'Found only a low-confidence PDF text match, so no highlight was drawn.',
                            debug: alignment.debug || null,
                        });
                    } else if (alignment) {
                        pdfChunkAlignmentRef.current = alignment;
                        targetPageAfterRender = alignment.firstPage;
                        pdfCurrentPageRef.current = alignment.firstPage;
                        setPdfHighlightState({
                            status: 'rendering',
                            pageNumber: alignment.firstPage,
                            message: `Rendering aligned chunk starting on page ${alignment.firstPage} (${alignment.debug?.method || 'matched'}, ${Math.round((alignment.debug?.confidence || 0) * 100)}% confidence)...`,
                            debug: alignment.debug || null,
                        });
                    } else {
                        setPdfHighlightState({
                            status: 'not-found',
                            pageNumber: null,
                            message: 'Could not align this indexed chunk to the PDF text layer.',
                            debug: null,
                        });
                    }
                }

                const rect = await waitForContainerSize(container);
                const containerWidth = rect.width || container.clientWidth || 800;
                const firstPage = await pdf.getPage(1);
                const baseViewport = firstPage.getViewport({ scale: 1 });
                const fitScale = Math.max(MIN_PDF_FIT_SCALE, Math.min(MAX_PDF_SCALE, (containerWidth - 24) / baseViewport.width));
                const scaleMultiplier = optimizedMode ? 0.9 : 1;
                const renderScale = Math.max(MIN_PDF_RENDER_SCALE, Math.min(4, fitScale * pdfRenderScale * scaleMultiplier));
                const thumbWidth = 140;
                const thumbScale = Math.max(0.15, Math.min(0.5, thumbWidth / baseViewport.width));
                const pageStates = Array.from({ length: pdf.numPages }, (_, index) => {
                    const viewport = baseViewport.clone({ scale: renderScale });
                    const wrapper = document.createElement('div');
                    wrapper.className = 'pdf-page pdf-page-placeholder';
                    wrapper.dataset.pageNumber = String(index + 1);
                    wrapper.style.setProperty('--scale-factor', `${viewport.scale}`);
                    wrapper.style.setProperty('--user-unit', '1');
                    wrapper.style.setProperty('--scale-round-x', '1px');
                    wrapper.style.setProperty('--scale-round-y', '1px');
                    wrapper.style.width = `${viewport.width}px`;
                    wrapper.style.height = `${viewport.height}px`;
                    wrapper.dataset.rendered = 'false';
                    return {
                        pageNumber: index + 1,
                        width: viewport.width,
                        height: viewport.height,
                        wrapper,
                        renderedScale: null,
                        rendering: false,
                        renderTask: null,
                        textRenderTask: null,
                        cleanup: () => {
                            if (wrapper.dataset.rendered === 'true') {
                                wrapper.replaceChildren();
                                wrapper.dataset.rendered = 'false';
                                wrapper.classList.add('pdf-page-placeholder');
                            }
                        }
                    };
                });

                const pageStatesByNumber = new Map(pageStates.map((state) => [state.pageNumber, state]));
                const pageFragment = document.createDocumentFragment();
                pageStates.forEach((state) => pageFragment.appendChild(state.wrapper));
                container.appendChild(pageFragment);

                const refreshWindowProgress = () => {
                    const { startPage, endPage, anchorPage } = pdfWindowProgressRef.current;
                    if (!startPage || !endPage || endPage < startPage) {
                        setPdfProgress((prev) => ({
                            ...prev,
                            isWindowRendering: false,
                            windowRenderedPages: 0,
                            windowTotalPages: 0,
                            windowStartPage: 0,
                            windowEndPage: 0,
                            windowAnchorPage: anchorPage || 0
                        }));
                        return;
                    }

                    let renderedCount = 0;
                    const totalCount = endPage - startPage + 1;
                    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber += 1) {
                        const state = pageStatesByNumber.get(pageNumber);
                        if (state?.wrapper?.dataset?.rendered === 'true' && state.renderedScale === renderScale) {
                            renderedCount += 1;
                        }
                    }

                    setPdfProgress((prev) => ({
                        ...prev,
                        isWindowRendering: renderedCount < totalCount,
                        windowRenderedPages: renderedCount,
                        windowTotalPages: totalCount,
                        windowStartPage: startPage,
                        windowEndPage: endPage,
                        windowAnchorPage: anchorPage
                    }));
                };

                const renderThumbnail = async (page, pageNumber) => {
                    if (cancelled) return;
                    const thumbViewport = page.getViewport({ scale: thumbScale });
                    const thumbWrapper = document.createElement('button');
                    thumbWrapper.type = 'button';
                    thumbWrapper.className = 'pdf-thumb';
                    thumbWrapper.dataset.pageNumber = String(pageNumber);
                    thumbWrapper.addEventListener('click', () => scrollToPdfPage(pageNumber));

                    const thumbCanvas = document.createElement('canvas');
                    const thumbContext = thumbCanvas.getContext('2d');
                    const thumbPixelRatio = getPdfCanvasPixelRatio(thumbViewport.width, thumbViewport.height);
                    const thumbOutputScale = thumbPixelRatio === 1 ? undefined : [thumbPixelRatio, 0, 0, thumbPixelRatio, 0, 0];
                    thumbCanvas.width = Math.floor(thumbViewport.width * thumbPixelRatio);
                    thumbCanvas.height = Math.floor(thumbViewport.height * thumbPixelRatio);
                    thumbCanvas.style.width = `${thumbViewport.width}px`;
                    thumbCanvas.style.height = `${thumbViewport.height}px`;
                    thumbWrapper.appendChild(thumbCanvas);

                    const thumbLabel = document.createElement('div');
                    thumbLabel.className = 'pdf-thumb-label';
                    thumbLabel.textContent = String(pageNumber);
                    thumbWrapper.appendChild(thumbLabel);

                    thumbsContainer.appendChild(thumbWrapper);

                    if (!thumbContext) return;
                    await page.render({ canvasContext: thumbContext, viewport: thumbViewport, transform: thumbOutputScale }).promise;
                    page.cleanup?.();
                    if (!cancelled) {
                        setPdfProgress((prev) => ({
                            ...prev,
                            renderedThumbs: Math.min(prev.totalThumbs, prev.renderedThumbs + 1)
                        }));
                    }
                };

                const renderPageNumber = async (pageNumber) => {
                    const pageState = pageStatesByNumber.get(pageNumber);
                    if (!pageState || pageState.rendering) return;
                    if (pageState.renderedScale === renderScale && pageState.wrapper.dataset.rendered === 'true') {
                        return;
                    }

                    pageState.rendering = true;
                    try {
                        const page = pageNumber === 1 ? firstPage : await pdf.getPage(pageNumber);
                        const viewport = page.getViewport({ scale: renderScale });
                        const pageWrapper = pageState.wrapper;
                        pageWrapper.style.setProperty('--scale-factor', `${viewport.scale}`);
                        pageWrapper.style.setProperty('--user-unit', '1');
                        pageWrapper.style.setProperty('--scale-round-x', '1px');
                        pageWrapper.style.setProperty('--scale-round-y', '1px');
                        pageWrapper.classList.remove('pdf-page-placeholder');

                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');

                        const enableAnnotationLayer = false;

                        let textLayerDiv = null;
                        let annotationLayerDiv = null;

                        if (enableTextLayer) {
                            textLayerDiv = document.createElement('div');
                            textLayerDiv.className = 'textLayer';
                        }

                        if (enableAnnotationLayer) {
                            annotationLayerDiv = document.createElement('div');
                            annotationLayerDiv.className = 'annotationLayer';
                        }

                        const pixelRatio = getPdfCanvasPixelRatio(viewport.width, viewport.height);
                        const outputScale = pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0];
                        canvas.width = Math.floor(viewport.width * pixelRatio);
                        canvas.height = Math.floor(viewport.height * pixelRatio);

                        pageState.width = viewport.width;
                        pageState.height = viewport.height;
                        pageWrapper.style.width = `${viewport.width}px`;
                        pageWrapper.style.height = `${viewport.height}px`;
                        canvas.style.width = `${viewport.width}px`;
                        canvas.style.height = `${viewport.height}px`;
                        if (textLayerDiv) {
                            textLayerDiv.style.width = `${viewport.width}px`;
                            textLayerDiv.style.height = `${viewport.height}px`;
                        }
                        if (annotationLayerDiv) {
                            annotationLayerDiv.style.width = `${viewport.width}px`;
                            annotationLayerDiv.style.height = `${viewport.height}px`;
                        }

                        pageWrapper.replaceChildren();
                        pageWrapper.appendChild(canvas);
                        if (annotationLayerDiv) pageWrapper.appendChild(annotationLayerDiv);
                        if (textLayerDiv) pageWrapper.appendChild(textLayerDiv);

                        if (!context) throw new Error('Canvas context unavailable');
                        pageState.renderTask = page.render({
                            canvasContext: context,
                            viewport,
                            transform: outputScale,
                            annotationMode: pdfjsLib.AnnotationMode?.ENABLE
                        });
                        await pageState.renderTask.promise;

                        if (enableTextLayer && textLayerDiv) {
                            try {
                                const textContent = await page.getTextContent();
                                if (pdfjsLib.TextLayer) {
                                    if (pdfjsLib.setLayerDimensions) {
                                        pdfjsLib.setLayerDimensions(textLayerDiv, viewport);
                                    }
                                    const textLayer = new pdfjsLib.TextLayer({
                                        textContentSource: textContent,
                                        container: textLayerDiv,
                                        viewport
                                    });
                                    pageState.textRenderTask = Promise.resolve(textLayer.render());
                                    await pageState.textRenderTask.catch((textErr) => {
                                        console.warn('Text layer render failed', textErr);
                                    });
                                    if (showChunkHighlightsRef.current && shouldPdfHighlightChunk) {
                                        applyPdfChunkHighlight(pageWrapper, textLayerDiv, pageNumber);
                                    }
                                }
                            } catch (textErr) {
                                console.warn('Text layer render failed', textErr);
                            }
                        }

                        if (enableAnnotationLayer && annotationLayerDiv) {
                            try {
                                if (pdfjsLib.AnnotationLayer) {
                                    const annotations = await page.getAnnotations({ intent: 'display' });
                                    const linkService = {
                                        getDestinationHash: () => '',
                                        getAnchorUrl: () => '',
                                        setHash: () => {}
                                    };
                                    if (pdfjsLib.setLayerDimensions) {
                                        pdfjsLib.setLayerDimensions(annotationLayerDiv, viewport);
                                    }
                                    pdfjsLib.AnnotationLayer.render({
                                        annotations,
                                        div: annotationLayerDiv,
                                        page,
                                        viewport,
                                        linkService,
                                        renderForms: true
                                    });
                                }
                            } catch (annotationErr) {
                                console.warn('Annotation layer render failed', annotationErr);
                            }
                        }

                        pageState.wrapper.dataset.rendered = 'true';
                        pageState.renderedScale = renderScale;
                        page.cleanup?.();
                        if (!cancelled && !renderedPageSet.has(pageNumber)) {
                            renderedPageSet.add(pageNumber);
                            setPdfProgress((prev) => ({
                                ...prev,
                                renderedPages: Math.min(prev.totalPages, prev.renderedPages + 1)
                            }));
                        }
                        if (!cancelled) {
                            refreshWindowProgress();
                        }
                    } catch (renderErr) {
                        if (renderErr?.name !== 'RenderingCancelledException') {
                            console.error('PDF page render failed', renderErr);
                            throw renderErr;
                        }
                    } finally {
                        pageState.renderTask = null;
                        pageState.textRenderTask = null;
                        pageState.rendering = false;
                    }
                };

                const clearPageNumber = (pageNumber) => {
                    const pageState = pageStatesByNumber.get(pageNumber);
                    if (!pageState || pageState.rendering === true) {
                        return;
                    }
                    pageState.cleanup();
                    pageState.renderedScale = null;
                    refreshWindowProgress();
                };

                const updateVisiblePages = async (activePageNumber = 1) => {
                    if (cancelled) return;

                    const renderRadius = PDF_RENDER_WINDOW_RADIUS;
                    const retainRadius = renderRadius + PDF_RETAIN_WINDOW_PADDING;
                    const renderStart = Math.max(1, activePageNumber - renderRadius);
                    const renderEnd = Math.min(pdf.numPages, activePageNumber + renderRadius);
                    const retainStart = Math.max(1, activePageNumber - retainRadius);
                    const retainEnd = Math.min(pdf.numPages, activePageNumber + retainRadius);

                    pdfWindowProgressRef.current = {
                        startPage: renderStart,
                        endPage: renderEnd,
                        anchorPage: activePageNumber
                    };
                    refreshWindowProgress();

                    for (let pageNumber = renderStart; pageNumber <= renderEnd; pageNumber += 1) {
                        if (cancelled) return;
                        await renderPageNumber(pageNumber);
                    }

                    for (const pageState of pageStates) {
                        if (pageState.pageNumber < retainStart || pageState.pageNumber > retainEnd) {
                            clearPageNumber(pageState.pageNumber);
                        }
                    }

                    refreshWindowProgress();
                };

                pdfVirtualizerRef.current = {
                    updateVisiblePages,
                    clear: () => {
                        pageStates.forEach((pageState) => pageState.cleanup());
                    }
                };

                await updateVisiblePages(targetPageAfterRender);
                if (!cancelled) {
                    setPdfRenderState({ isRendering: false, error: null });
                    setPdfProgress((prev) => ({
                        ...prev,
                        isLoadingDocument: false,
                        downloadPercent: 100
                    }));
                }

                if (!cancelled) {
                    flushSync(() => {
                        setPdfDisplayedScale(pdfRenderScale);
                    });
                    if (!showChunkHighlightsRef.current || !shouldPdfHighlightChunk || !chunkTargetState.data?.chunk_text) {
                        restorePdfScrollAnchor(scrollAnchor, targetPageAfterRender);

                        requestAnimationFrame(() => {
                            restorePdfScrollAnchor(scrollAnchor, targetPageAfterRender);
                        });
                    }

                    setPdfProgress((prev) => ({
                        ...prev,
                        isLoadingDocument: false,
                        downloadPercent: 100
                    }));
                }

                backgroundThumbnailPromise = (async () => {
                    for (let pageNumber = 1; pageNumber <= thumbnailTotal; pageNumber += 1) {
                        if (cancelled) return;
                        const page = pageNumber === 1 ? firstPage : await pdf.getPage(pageNumber);
                        await renderThumbnail(page, pageNumber);
                        if (pageNumber % 6 === 0) {
                            await yieldToBrowser();
                        }
                    }
                })().catch((thumbErr) => {
                    if (!cancelled) {
                        console.warn('Thumbnail rendering failed', thumbErr);
                    }
                });
            } catch (err) {
                if (!cancelled) {
                    setPdfRenderState({ isRendering: false, error: err?.message || 'Unable to render PDF' });
                    setPdfProgress((prev) => ({
                        ...prev,
                        isLoadingDocument: false
                    }));
                }
            }
        };

        renderPdf();

        return () => {
            cancelled = true;
            pdfVirtualizerRef.current?.clear?.();
            pdfVirtualizerRef.current = null;
            if (loadingTask?.destroy) {
                try {
                    loadingTask.destroy();
                } catch (destroyError) {
                    console.warn('PDF loading task cleanup failed', destroyError);
                }
            }
            if (backgroundThumbnailPromise?.catch) {
                backgroundThumbnailPromise.catch(() => {});
            }
            setPdfRenderState((prev) => ({ ...prev, isRendering: false }));
        };
    }, [viewerExtension, activePdfUrl, pdfRenderScale, chunkTargetState.data?.chunk_text]);

    useEffect(() => {
        if (viewerExtension !== "pdf") return;

        const container = pdfScrollRef.current;
        if (!container) return;

        const updatePdfIndicator = () => {
            const clientHeight = container.clientHeight;
            const anchorY = container.scrollTop + clientHeight * 0.49999;
            const pages = container.querySelectorAll('.pdf-page');
            const thumbsContainer = pdfThumbsContainerRef.current;
            const thumbs = thumbsContainer ? thumbsContainer.querySelectorAll('.pdf-thumb') : [];

            if (!pages.length) {
                setPdfCurrentPage(1);
                return;
            }

            let activePage = 1;
            pages.forEach((page) => {
                const element = page;
                const top = element.offsetTop;
                const bottom = top + element.offsetHeight;
                if (anchorY >= top && anchorY < bottom) {
                    activePage = Number(element.dataset.pageNumber || 1);
                }
            });

            setPdfCurrentPage(activePage);
            pdfVirtualizerRef.current?.updateVisiblePages?.(activePage);

            if (thumbs.length) {
                let activeThumb = null;
                thumbs.forEach((thumb) => {
                    const isActive = Number(thumb.dataset.pageNumber || 0) === activePage;
                    thumb.classList.toggle('active', isActive);
                    if (isActive) {
                        activeThumb = thumb;
                    }
                });

                if (activeThumb && lastActiveThumbRef.current !== activeThumb) {
                    activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                    lastActiveThumbRef.current = activeThumb;
                }
            }
        };

        updatePdfIndicator();
        container.addEventListener('scroll', updatePdfIndicator, { passive: true });
        window.addEventListener('resize', updatePdfIndicator);

        return () => {
            container.removeEventListener('scroll', updatePdfIndicator);
            window.removeEventListener('resize', updatePdfIndicator);
        };
    }, [viewerExtension, pdfRenderState.isRendering]);

    useEffect(() => {
        if (!canUseRedactionMode) return;

        const container = pdfScrollRef.current;
        if (!container) return;

        container.addEventListener('mouseup', capturePdfRedactionSelection);
        container.addEventListener('touchend', capturePdfRedactionSelection);

        return () => {
            container.removeEventListener('mouseup', capturePdfRedactionSelection);
            container.removeEventListener('touchend', capturePdfRedactionSelection);
        };
    }, [canUseRedactionMode, capturePdfRedactionSelection]);

    useEffect(() => {
        if (!canUseRedactionMode) return;
        window.requestAnimationFrame(drawPdfRedactionOverlays);
    }, [canUseRedactionMode, drawPdfRedactionOverlays, pdfRenderState.isRendering]);

    useEffect(() => {
        if (!canUseRedactionMode) return;

        const storageKey = getRedactionDraftStorageKey();
        if (!storageKey) return;

        try {
            const raw = window.localStorage.getItem(storageKey);
            if (!raw) return;

            const parsed = JSON.parse(raw);
            const storedBoxes = Array.isArray(parsed?.boxes) ? parsed.boxes : [];
            if (storedBoxes.length > 0) {
                setRedactionBoxes(storedBoxes);
            }

            if (parsed?.saved_at) {
                setRedactionDraftState((prev) => ({
                    ...prev,
                    lastSavedAt: parsed.saved_at,
                    error: null,
                }));
            }
        } catch (error) {
            console.warn('Unable to load redaction draft', error);
            setRedactionDraftState((prev) => ({
                ...prev,
                error: 'Unable to load saved redaction draft.',
            }));
        }
    }, [canUseRedactionMode, getRedactionDraftStorageKey]);

    useEffect(() => {
        if (viewerExtension !== "pdf" || isPageEditing) return;
        setPageInputValue(String(pdfCurrentPage));
    }, [viewerExtension, isPageEditing, pdfCurrentPage]);

    useEffect(() => {
        if (viewerExtension !== "pdf") return;

        const handlePdfKeyNavigation = (event) => {
            const activeElement = document.activeElement;
            const tagName = activeElement?.tagName;
            const isEditableElement =
                activeElement?.isContentEditable ||
                tagName === 'INPUT' ||
                tagName === 'TEXTAREA' ||
                tagName === 'SELECT';

            if (isEditableElement) return;

            if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"].includes(event.key)) {
                return;
            }

            const maxPage = Math.max(1, Number(totalPdfPages || pageCount || 1));
            if (maxPage < 1) return;

            let nextPage = pdfCurrentPage;

            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'PageUp') {
                nextPage = Math.max(1, pdfCurrentPage - 1);
            } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'PageDown') {
                nextPage = Math.min(maxPage, pdfCurrentPage + 1);
            } else if (event.key === 'Home') {
                nextPage = 1;
            } else if (event.key === 'End') {
                nextPage = maxPage;
            }

            if (nextPage !== pdfCurrentPage) {
                event.preventDefault();
                goToPdfPage(nextPage);
            }
        };

        window.addEventListener('keydown', handlePdfKeyNavigation);
        return () => {
            window.removeEventListener('keydown', handlePdfKeyNavigation);
        };
    }, [viewerExtension, pageCount, pdfCurrentPage, totalPdfPages]);

    if (loading) {
        return (
            <div className="file-viewer-container">
                <div className="file-viewer-loading">Loading file...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="file-viewer-container">
                <div className="file-viewer-error">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={handleBackOrClose} className="btn-back">Back</button>
                </div>
            </div>
        );
    }

    return (
        <div className={`file-viewer-container page-container full ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div className="file-viewer-header">
                <div className="file-viewer-nav">
                    <button onClick={handleBackOrClose} className="btn-back">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <h2 className="file-title">{file.file_name}</h2>
                    {/*file?.page_count && <span className='subtext'>Pages: {file.page_count}</span>*/}
                </div>
                <div className="file-viewer-actions">
                    <button onClick={handleDownload} className="btn-action" title="Download file">
                        <Download size={16} /> Download
                    </button>
                    {shouldPdfHighlightChunk && (
                        <div className="file-viewer-highlight-toggle" title="Show or hide chunk highlight overlays">
                            <Checkbox
                                value={showChunkHighlights}
                                onChange={() => setShowChunkHighlights((current) => !current)}
                                mini
                                label="Highlights"
                            />
                        </div>
                    )}
                    {isOfficeDocument && hasFileRecordId && (
                        <button
                            onClick={handleOfficeEdit}
                            className="btn-action"
                            title="Edit in Office"
                            disabled={officeEditState.isLoading}
                        >
                            <Pen size={16} /> {officeEditState.isLoading ? 'Opening...' : 'Edit in Office'}
                        </button>
                    )}
                    {canUseRedactionMode && (
                        <button
                            className="btn-action redact-complete-btn"
                            title={redactionBoxes.length > 0 ? 'Save redaction draft' : 'Select text to save redactions'}
                            onClick={handleCompleteRedaction}
                            disabled={redactionBoxes.length === 0 || redactionDraftState.isSaving}
                        >
                            <ScanLine size={16} /> {redactionDraftState.isSaving ? 'Saving...' : `Save Redaction${redactionBoxes.length > 0 ? ` (${redactionBoxes.length})` : ''}`}
                        </button>
                    )}
                    {hasFileRecordId && (
                        <button onClick={handleShare} className="btn-action" title="Share file">
                            <Share size={16} /> Share
                        </button>
                    )}
                </div>
            </div>

            {/* Fallback download button for PDFs where toolbar might be hidden */}
            <div className="file-viewer-pdf-fallback">
                <button onClick={handleDownload} className="btn-action-fallback" title="Download this file">
                    <Download size={14} /> Download
                </button>
            </div>

            {shouldPdfHighlightChunk && (
                <div className="file-viewer-chunk-target-panel">
                    <div className="file-viewer-chunk-target-meta">
                        <strong>Matched chunk</strong>
                        {temporaryPdfState.isLoading && <span>Preparing temporary PDF preview...</span>}
                        {!temporaryPdfState.isLoading && temporaryPdfState.url && <span>Viewing temporary converted PDF</span>}
                        {chunkTargetState.isLoading && <span>Loading indexed chunk...</span>}
                        {pdfHighlightState.message && <span>{pdfHighlightState.message}</span>}
                        {temporaryPdfState.error && <span className="file-viewer-chunk-target-error">{temporaryPdfState.error}</span>}
                        {chunkTargetState.error && <span className="file-viewer-chunk-target-error">{chunkTargetState.error}</span>}
                    </div>
                    {pdfHighlightState.debug && (
                        <div className="file-viewer-chunk-target-debug">
                            <span>Match: {pdfHighlightState.debug.method} · {Math.round((pdfHighlightState.debug.confidence || 0) * 100)}% · PDF {pdfHighlightState.debug.selectedLength} chars / chunk {pdfHighlightState.debug.targetLength} chars{pdfHighlightState.debug.expectedPages ? ` · expected pages ${pdfHighlightState.debug.expectedPages}` : ''}</span>
                            {pdfHighlightState.debug.selectedPreview && <span>PDF text: “{pdfHighlightState.debug.selectedPreview}”</span>}
                        </div>
                    )}
                </div>
            )}

            {canUseRedactionMode && (
                <div className="file-viewer-redaction-panel">
                    <div className="file-viewer-redaction-meta">
                        <strong>Redaction mode</strong>
                        <span>Select text in the PDF to add black preview boxes.</span>
                        <span>{redactionBoxes.length} box{redactionBoxes.length === 1 ? '' : 'es'} selected.</span>
                        {redactionBoxes.length > 0 && (
                            <button type="button" className="file-viewer-redaction-clear" onClick={() => setRedactionBoxes([])}>
                                Clear
                            </button>
                        )}
                        <span>Click a black redaction box to remove the full grouped selection from that highlight action.</span>
                        {redactionDraftState.lastSavedAt && (
                            <span>Draft saved {new Date(redactionDraftState.lastSavedAt).toLocaleString()}.</span>
                        )}
                        {redactionDraftState.error && (
                            <span className="file-viewer-redaction-error">{redactionDraftState.error}</span>
                        )}
                    </div>
                </div>
            )}

            <div className="file-viewer-content">
                {(() => {
                    if (!file?.url) {
                        return <p className="loading-content">Loading file content...</p>;
                    }

                    if (shouldApplyChunkHighlights && fileExtension !== "pdf" && canConvertToPdfPreview && temporaryPdfState.isLoading) {
                        return (
                            <div className="loading-content">
                                <p>Preparing temporary PDF preview...</p>
                                <p style={{ fontSize: "14px", color: "var(--text-muted)", marginTop: "8px" }}>
                                    The matched chunk will be highlighted after conversion completes.
                                </p>
                            </div>
                        );
                    }
                    
                    // PDF Files - Using PDF proxy like DocumentSection
                    if (viewerExtension === "pdf") {
                        const displayPercent = Math.round((pdfScale / BASE_PDF_SCALE) * 100);
                        const liveZoomRatio = Math.max(0.5, Math.min(3, Number((pdfScale / pdfDisplayedScale).toFixed(3)) || 1));
                        const windowPercent = pdfProgress.windowTotalPages > 0
                            ? Math.max(0, Math.min(100, Math.round((pdfProgress.windowRenderedPages / pdfProgress.windowTotalPages) * 100)))
                            : 100;
                        const renderPercent = pdfProgress.totalPages > 0
                            ? Math.max(0, Math.min(100, Math.round((pdfProgress.renderedPages / pdfProgress.totalPages) * 100)))
                            : pdfProgress.downloadPercent;
                        const progressPercent = pdfProgress.isLoadingDocument
                            ? pdfProgress.downloadPercent
                            : (pdfProgress.isWindowRendering ? windowPercent : renderPercent);
                        const showProgressBar = pdfProgress.isLoadingDocument || pdfRenderState.isRendering;
                        if (shouldUseNativePdfViewer) {
                            return (
                                <div className="pdf-viewer-wrapper">
                                    <iframe
                                        src={activePdfUrl}
                                        title={file.file_name}
                                        className="native-pdf-viewer"
                                    />
                                </div>
                            );
                        }
                        return (
                            <div className="pdf-viewer-wrapper">
                                {showProgressBar && (
                                    <div className="pdf-progress-overlay" aria-live="polite">
                                        <div className="pdf-progress-bar-track">
                                            <div
                                                className="pdf-progress-bar-fill"
                                                style={{ width: `${progressPercent}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <aside className="pdf-thumbnails" aria-label="PDF page thumbnails">
                                    <div className="pdf-thumbnails-title">Pages</div>
                                    <div className="pdf-thumbnails-list" ref={pdfThumbsContainerRef} />
                                </aside>
                                <div className="pdf-viewer-main">
                                    <div className="pdf-zoom-controls">
                                        {!shouldUseNativePdfViewer && activePdfUrl && (
                                            <button
                                                type="button"
                                                className="pdf-native-btn"
                                                onClick={() => setForceNativePdfViewer(true)}
                                                aria-label="View with native PDF viewer"
                                                title="View with native PDF viewer"
                                            >
                                                Native
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="pdf-zoom-btn"
                                            onClick={() => setPdfScale((prev) => Math.max(MIN_PDF_SCALE, Number((prev - 0.1).toFixed(2))))}
                                            aria-label="Zoom out"
                                        >
                                            −
                                        </button>
                                        {isZoomEditing ? (
                                            <input
                                                className="pdf-zoom-input"
                                                type="number"
                                                min={50}
                                                max={600}
                                                step={10}
                                                autoFocus
                                                value={zoomInputValue}
                                                onChange={(event) => setZoomInputValue(event.target.value)}
                                                onFocus={(event) => event.target.select()}
                                                onBlur={commitZoomInput}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        commitZoomInput();
                                                    }
                                                    if (event.key === 'Escape') {
                                                        setIsZoomEditing(false);
                                                    }
                                                }}
                                                aria-label="Zoom percentage"
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                className="pdf-zoom-label pdf-zoom-label-btn"
                                                onClick={() => {
                                                    setZoomInputValue(String(displayPercent));
                                                    setIsZoomEditing(true);
                                                }}
                                                aria-label="Edit zoom percentage"
                                            >
                                                {displayPercent}%
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="pdf-zoom-btn"
                                            onClick={() => setPdfScale((prev) => Math.min(MAX_PDF_SCALE, Number((prev + 0.1).toFixed(2))))}
                                            aria-label="Zoom in"
                                        >
                                            +
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-zoom-btn pdf-print-btn"
                                            onClick={handlePdfPrint}
                                            aria-label="Print PDF"
                                        >
                                            Print
                                        </button>
                                    </div>
                                    <div className="pdf-scroll-container" ref={pdfScrollRef}>
                                        <div
                                            className="pdf-canvas-container"
                                            ref={pdfCanvasContainerRef}
                                            style={{ zoom: liveZoomRatio }}
                                        />
                                    </div>
                                    {pdfRenderState.error && (
                                        <div className="pdf-render-state error">{pdfRenderState.error}</div>
                                    )}
                                    {totalPdfPages > 0 && (
                                        <div className="pdf-page-indicator-container" aria-live="polite">
                                            <div className="pdf-page-indicator">
                                                {isPageEditing ? (
                                                    <>
                                                        <input
                                                            className="pdf-page-input"
                                                            type="number"
                                                            min={1}
                                                            max={totalPdfPages}
                                                            step={1}
                                                            autoFocus
                                                            value={pageInputValue}
                                                            onChange={(event) => setPageInputValue(event.target.value)}
                                                            onFocus={(event) => event.target.select()}
                                                            onBlur={commitPageInput}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    commitPageInput();
                                                                }
                                                                if (event.key === 'Escape') {
                                                                    setPageInputValue(String(pdfCurrentPage));
                                                                    setIsPageEditing(false);
                                                                }
                                                            }}
                                                            aria-label="Current PDF page"
                                                        />
                                                        <span className="pdf-page-indicator-separator"> / </span>
                                                        <span className="pdf-page-indicator-total">{totalPdfPages}</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="pdf-page-indicator-button"
                                                            onClick={() => {
                                                                setPageInputValue(String(pdfCurrentPage));
                                                                setIsPageEditing(true);
                                                            }}
                                                            aria-label="Edit current PDF page"
                                                            title="Jump to page"
                                                        >
                                                            {pdfCurrentPage}
                                                        </button>
                                                        <span className="pdf-page-indicator-separator"> / </span>
                                                        <span className="pdf-page-indicator-total">{totalPdfPages}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }
                    
                    // EML / MSG files - use cached PDF full preview by default
                    else if (["msg", "eml"].includes(fileExtension)) {
                        return (
                            <div className="email-viewer-container">
                                {emailPdfPreviewState.isLoading && (
                                    <div className="loading-content">
                                        <p>Preparing cached PDF preview...</p>
                                        <p style={{ fontSize: "14px", color: "var(--text-muted)", marginTop: "8px" }}>
                                            {fileExtension.toUpperCase()} files are converted once and cached for reuse.
                                        </p>
                                    </div>
                                )}

                                {!emailPdfPreviewState.isLoading && emailPdfPreviewState.url && (
                                    <div style={{ padding: '20px', overflow: 'auto', height: `calc(100vh - ${(navVariant === 'full' || navVariant === 'compact') ? '80px' : '140px'})` }}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <button onClick={handleDownload} className="btn-action">
                                                <Download size={16} /> Download
                                            </button>
                                        </div>
                                        <iframe
                                            src={emailPdfPreviewState.url}
                                            title={`${file.file_name} PDF Preview`}
                                            style={{
                                                width: '100%',
                                                height: 'calc(100% - 48px)',
                                                minHeight: '75vh',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '8px',
                                                backgroundColor: 'var(--secondary-color)'
                                            }}
                                        />
                                    </div>
                                )}

                                {!emailPdfPreviewState.isLoading && !emailPdfPreviewState.url && emailPreviewState.error && (
                                    <div className="error-content" style={{ padding: "16px", textAlign: "center" }}>
                                        <p style={{ color: "var(--priority-color)", marginBottom: "10px" }}>
                                            ⚠ {emailPdfPreviewState.error || emailPreviewState.error}
                                        </p>
                                        <button onClick={handleDownload} className="btn-action">
                                            <Download size={16} /> Download {fileExtension.toUpperCase()}
                                        </button>
                                    </div>
                                )}

                                {!emailPdfPreviewState.isLoading && !emailPdfPreviewState.url && emailPreviewData && (
                                    <div style={{ padding: '20px', overflow: 'auto', height: `calc(100vh - ${(navVariant === 'full' || navVariant === 'compact') ? '80px' : '140px'})` }}>
                                        <EmailPreviewContent
                                            preview={emailPreviewData}
                                            fallbackTitle={file.file_name}
                                            containerStyle={{ marginBottom: '16px', padding: '16px', borderRadius: '8px' }}
                                            bodyStyle={{ minHeight: '240px' }}
                                        />
                                        <div style={{ marginTop: '12px' }}>
                                            <button onClick={handleDownload} className="btn-action">
                                                <Download size={16} /> Download
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }
                    
                    // Image Files (including HEIC)
                    else if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "heic"].includes(fileExtension)) {
                        // Special handling for HEIC files
                        if (fileExtension === "heic") {
                            if (heicConversionState.isConverting) {
                                return (
                                    <div className="image-viewer-container" style={{ padding: "50px", textAlign: "center" }}>
                                        <div className="loading-content">
                                            <p>Converting HEIC image...</p>
                                            <p style={{ fontSize: "14px", color: "var(--text-muted)", marginTop: "10px" }}>
                                                This may take a moment for large images.
                                            </p>
                                        </div>
                                    </div>
                                );
                            }
                            
                            if (heicConversionState.conversionError) {
                                return (
                                    <div className="image-viewer-container" style={{ padding: "50px", textAlign: "center" }}>
                                        <div className="error-content">
                                            <p style={{ color: "var(--priority-color)", marginBottom: "10px" }}>
                                                ⚠ Failed to convert HEIC image
                                            </p>
                                            <p style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "20px" }}>
                                                Error: {heicConversionState.conversionError}
                                            </p>
                                            <button onClick={handleDownload} className="btn-action">
                                                Download Original File
                                            </button>
                                        </div>
                                    </div>
                                );
                            }
                            
                            if (heicConversionState.convertedUrl) {
                                return (
                                    <div className="image-viewer-container" style={{ 
                                        display: "flex", 
                                        flexDirection: "column",
                                        justifyContent: "flex-start", 
                                        alignItems: "center", 
                                        padding: "20px",
                                        overflow: "auto",
                                        minHeight: "calc(100vh - 120px)"
                                    }}>
                                        <img 
                                            src={heicConversionState.convertedUrl} 
                                            alt={file.file_name} 
                                            className="image-preview" 
                                            style={{ 
                                                maxWidth: "100%", 
                                                height: "auto", 
                                                maxHeight: "calc(100vh - 200px)", 
                                                objectFit: "contain",
                                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div className="image-fallback" style={{ display: 'none', textAlign: 'center', padding: '50px' }}>
                                            <p>Unable to display converted image</p>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '10px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                            Converted from HEIC format
                                        </div>
                                    </div>
                                );
                            }
                            
                            // Fallback while waiting
                            return (
                                <div className="image-viewer-container" style={{ padding: "50px", textAlign: "center" }}>
                                    <p>Preparing HEIC image...</p>
                                </div>
                            );
                        }
                        
                        // Standard image handling for other formats
                        return (
                            <div className="image-viewer-container" style={{ 
                                display: "flex", 
                                justifyContent: "center", 
                                alignItems: "flex-start", 
                                padding: "20px",
                                overflow: "auto",
                                minHeight: "calc(100vh - 120px)"
                            }}>
                                <img 
                                    src={file.url} 
                                    alt={file.file_name} 
                                    className="image-preview" 
                                    style={{ 
                                        maxWidth: "100%", 
                                        height: "auto", 
                                        maxHeight: "calc(100vh - 160px)", 
                                        objectFit: "contain",
                                        borderRadius: '6px',
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="image-fallback" style={{ display: 'none', textAlign: 'center', padding: '50px' }}>
                                    <p>Unable to load image</p>
                                </div>
                            </div>
                        );
                    }
                    
                    // Audio Files
                    else if (["mp3", "wav", "ogg", "aac", "flac"].includes(fileExtension)) {
                        return (
                            <div className="audio-viewer-container" style={{ padding: "20px", textAlign: "center" }}>
                                <audio 
                                    controls 
                                    src={file.url} 
                                    style={{ width: "100%", maxWidth: "600px", marginBottom: "20px" }}
                                    preload="metadata"
                                />
                                <div className="file-info">
                                    <p><strong>Type:</strong> Audio file ({fileExtension.toUpperCase()})</p>
                                    <p><strong>File:</strong> {file.file_name}</p>
                                </div>
                            </div>
                        );
                    }
                    
                    // Video Files
                    else if (["mp4", "webm", "mov"].includes(fileExtension)) {
                        return (
                            <div className="video-viewer-container" style={{ padding: "20px", textAlign: "center" }}>
                                <video 
                                    controls 
                                    src={file.url} 
                                    style={{ 
                                        width: "100%", 
                                        maxHeight: "calc(100vh - 200px)", 
                                        marginBottom: "20px" 
                                    }}
                                    preload="metadata"
                                />
                                <div className="file-info">
                                    <p><strong>Type:</strong> Video file ({fileExtension.toUpperCase()})</p>
                                    <p><strong>File:</strong> {file.file_name}</p>
                                </div>
                            </div>
                        );
                    }
                    
                    // Calendar Files (ICS)
                    else if (fileExtension === "ics") {
                        return (
                            <div className="calendar-viewer-container">
                                <pre className="file-preview-cont">{fileContent || "Loading calendar content..."}</pre>
                            </div>
                        );
                    }
                    
                    // Text Files
                    else if (["txt", "log", "md", "json", "xml", "csv", "html", "htm"].includes(fileExtension)) {
                        return (
                            <div className="text-viewer-container">
                                <pre className="file-preview-cont" style={{ 
                                    padding: "20px", 
                                    whiteSpace: "pre-wrap", 
                                    fontSize: "14px",
                                    lineHeight: "1.5",
                                    height: `calc(100vh - ${(navVariant === 'full' || navVariant === 'compact') ? '80px' : '140px'})`,
                                    overflow: "auto"
                                }}>
                                    {fileContent || "Loading text content..."}
                                </pre>
                            </div>
                        );
                    }
                    
                    // Office Documents (Word, Excel, PowerPoint)
                    else if (isOfficeDocument) {
                        const officePreviewUrl = file.office_preview_url || file.url;
                        const encodedUrl = encodeURIComponent(officePreviewUrl);
                        return (
                            <div className="office-viewer-container">
                                <iframe
                                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`}
                                    title={file.file_name}
                                    className="office-viewer"
                                    style={{
                                        width: "100%",
                                        height: "calc(100vh - 120px)",
                                        border: "1px solid var(--border-color)",
                                        borderRadius: '6px'
                                    }}
                                />
                                {!encodedUrl && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', textAlign: 'center', padding: '10px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                        If the document doesn't display properly, download it to view locally.
                                        <button onClick={handleDownload} className="btn-action" style={{ marginLeft: '12px' }}>
                                            <Download size={14} /> Download
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    }
                    
                    // Unsupported file types
                    else {
                        return (
                            <div className="unsupported-viewer-container" style={{ 
                                padding: "50px", 
                                textAlign: "center" 
                            }}>
                                <div className="file-info">
                                    <h3>{file.file_name}</h3>
                                    <p><strong>Type:</strong> {fileExtension.toUpperCase()}</p>
                                    <p><strong>Size:</strong> {file.file_size || 'Unknown'}</p>
                                    <p className="unsupported-message">⚠ Preview not available for this file type.</p>
                                </div>
                                <div style={{ marginTop: "20px" }}>
                                    <button onClick={handleDownload} className="btn-action">
                                        Download to view
                                    </button>
                                </div>
                            </div>
                        );
                    }
                })()}
            </div>

            {showShareModal && (
                <ShareModal
                    onClose={() => {
                        setShowShareModal(false);
                        setShareLink("");
                    }}
                    target={shareTarget}
                    createShareableLink={createShareableLink}
                    shareLink={shareLink}
                    caseName={file?.case_name || 'Unknown Case'}
                />
            )}
        </div>
    );
};

export default FileViewer;
